/**
 * Chat Page Component
 *
 * Main interface for interacting with the QA AI Agent
 * Layout: Chat history sidebar (4/12) + Chat area (8/12) - Similar to Claude
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Tooltip,
  Collapse,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as AgentIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Lightbulb as TipsIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugIcon,
  Assessment as CoverageIcon,
  Assignment as PlanIcon,
  Summarize as ReportIcon,
  Storage as DataIcon,
  Cloud as EnvironmentIcon,
  Api as ApiIcon,
  Build as AutomationIcon,
  PlayArrow as PlayIcon,
  SmartToy as AiTestingIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Layers as LayersIcon,
  BarChart as UsageIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import apiService, { TOOL_CATEGORIES, QUICK_ACTIONS, submitHitlResponse, getPendingHitlRequests } from '../services/api.service';
import testingService from '../services/testing.service';
import sessionService from '../services/session.service';
import useAuth from '../hooks/useAuth';
import { useTestingSession } from '../hooks/useTestingSession';
import { ChatMessage, StreamEvent, ChatSession, SqlDataPayload, NLTestPlan, SiteAnalysisProgress, GeneratedCodePayload, TMSEntityRef, TestingSessionCreatedEvent, ProjectContextSetEvent, TMSContextPrompt, TestPlanDraft, CodebaseSnapshot, CodebaseTestResults, APIStepResult, APIResponseDetails, AgentToolActivity, SlavePreflightData, TMSIntegrationInfo, SlavePreflightEndpoint, SlavePreflightConfirmOptions } from '../types';
import { Project, listProjects } from '../services/project.service';
import ProjectSelector from '../components/ProjectSelector';
import RepositoryPicker from '../components/RepositoryPicker';
import AuthorizedDomainsManager from '../components/AuthorizedDomainsManager';
import TestBriefPanel from '../components/TestBriefPanel';
import EditableTestPlanCard from '../components/EditableTestPlanCard';
import ApiWorkbenchPanel from '../components/ApiWorkbenchPanel';
import { FiberManualRecord as RecordIcon } from '@mui/icons-material';
import MessageFeedback from '../components/MessageFeedback';
import ChatHistorySidebar from '../components/ChatHistorySidebar';
import SqlDataVisualizer from '../components/SqlDataVisualizer';
import NLTestStepsViewer from '../components/NLTestStepsViewer';
import NLTestApprovalCard from '../components/NLTestApprovalCard';
import NLExecutionProgress from '../components/NLExecutionProgress';
import NLVisualAssertionResult from '../components/NLVisualAssertionResult';
import NLExplorationResult from '../components/NLExplorationResult';
import SiteAnalysisDashboard from '../components/SiteAnalysisDashboard';
import GeneratedCodeViewer from '../components/GeneratedCodeViewer';
import CodebaseAnalysisViewer from '../components/CodebaseAnalysisViewer';
import CodebaseTestResultsCard from '../components/CodebaseTestResultsCard';
import BrowserPane from '../components/BrowserPane';
import UITestingPanel from '../components/UITestingPanel';
import APIExecutionPanel from '../components/APIExecutionPanel';
import SecurityPanel from '../components/SecurityPanel';
import SiteTreeViewer from '../components/SiteTreeViewer';
import TestingEventCard from '../components/TestingEventCards';
import TMSContextCard from '../components/TMSContextCard';
import SlavePreflightCard from '../components/SlavePreflightCard';
import TestPlanEditor from '../components/TestPlanEditor';
import TestPlanDetailPanel from '../components/TestPlanDetailPanel';
import CoverageDashboardCard from '../components/CoverageDashboardCard';
import IntegrationsPage from './settings/IntegrationsPage';
import {
  applySlaveComplete,
  walletNoticeFromEvent,
  hitlGateFromToolStart,
  isHitlTool,
  isLoadCapTool,
  loadLargeGate,
  loadCapToolKey,
  isOwnLoadLargeRequest,
  isServerComposedHitlTool,
  waitForPendingHitl,
} from './chatEventHelpers';
import { UsageChip } from '../components/usage/UsageChip';
import { WalletBanner, type StreamNotice } from '../components/usage/WalletBanner';

// ============================================================================
// Helper Components
// ============================================================================

/** Inspect a ChatMessage for non-empty artifact fields. */
const getMessageArtifactInfo = (msg: ChatMessage): { hasArtifacts: boolean; types: string[]; count: number } => {
  if (msg.role !== 'assistant') return { hasArtifacts: false, types: [], count: 0 };
  const types: string[] = [];
  if (msg.slaveToolEvents && msg.slaveToolEvents.length > 0) types.push('API Results');
  if (msg.generatedCode) types.push('Generated Code');
  if (msg.sqlData) types.push('SQL Data');
  if (msg.testPlanDraft) types.push('Test Plan');
  if (msg.securityScanResult) types.push('Security Scan');
  if (msg.coverageDashboard) types.push('Coverage');
  if (msg.nlTestPlan) types.push('NL Test Plan');
  if (msg.codebaseTestResults) types.push('Test Results');
  if (msg.slaveComplete) {
    if (!types.includes('API Results')) types.push('Agent Session');
  }
  return { hasArtifacts: types.length > 0, types, count: types.length };
};

const getToolIcon = (iconName: string) => {
  const icons: Record<string, React.ReactNode> = {
    bug: <BugIcon />,
    coverage: <CoverageIcon />,
    plan: <PlanIcon />,
    report: <ReportIcon />,
    data: <DataIcon />,
    environment: <EnvironmentIcon />,
    api: <ApiIcon />,
    automation: <AutomationIcon />,
    ai_testing: <AiTestingIcon />,
  };
  return icons[iconName] || <AgentIcon />;
};

interface MessageBubbleProps {
  message: ChatMessage;
  onApprove?: (approvalId: string) => void;
  onReject?: (approvalId: string) => void;
  activeTestingSessionId?: string;
  onTMSContextSelect?: (prompt: TMSContextPrompt, choice: string, planId?: number, scope?: string, testCaseIds?: number[]) => void;
  onSlavePreflightConfirm?: (originalRequest: string, options?: SlavePreflightConfirmOptions) => Promise<void>;
  preflightProjectId?: string;
  canAuthorizeDomain?: boolean;
  isSelected?: boolean;
  onSelectMessage?: (messageId: string) => void;
  artifactInfo?: { hasArtifacts: boolean; types: string[]; count: number };
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onApprove, onReject, activeTestingSessionId, onTMSContextSelect, onSlavePreflightConfirm, preflightProjectId, canAuthorizeDomain, isSelected, onSelectMessage, artifactInfo }) => {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(message.content);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = message.content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        elevation={1}
        onClick={!isUser && artifactInfo?.hasArtifacts && onSelectMessage ? () => onSelectMessage(message.id) : undefined}
        sx={{
          maxWidth: '85%',
          p: 2,
          backgroundColor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          position: 'relative',
          ...(isSelected ? {
            borderLeft: '3px solid',
            borderColor: 'primary.main',
          } : {}),
          ...(!isUser && artifactInfo?.hasArtifacts ? { cursor: 'pointer' } : {}),
        }}
      >
        {!isUser && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AgentIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="text.secondary">
              QA Agent
            </Typography>
            {message.status === 'streaming' && <CircularProgress size={12} />}
          </Box>
        )}

        {/* Status Alerts Banner (Pillar 4) */}
        {!isUser && message.statusAlerts && message.statusAlerts.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {message.statusAlerts.map((alert, idx) => (
              <Alert
                key={alert.id || idx}
                severity={alert.severity === 'critical' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
                sx={{ mb: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.85rem' } }}
              >
                <strong>{alert.alert_type.replace(/_/g, ' ').toUpperCase()}</strong>: {alert.title}
              </Alert>
            ))}
          </Box>
        )}

        {/* SQL Data Visualization (above text) */}
        {!isUser && message.sqlData && (
          <SqlDataVisualizer data={message.sqlData} />
        )}

        {/* NL Test Steps Viewer */}
        {!isUser && message.nlTestPlan && (
          <NLTestStepsViewer
            plan={message.nlTestPlan}
            stepResults={message.nlExecutionProgress?.step_results}
          />
        )}

        {/* NL Execution Progress */}
        {!isUser && message.nlExecutionProgress && (
          <NLExecutionProgress progress={message.nlExecutionProgress} />
        )}

        {/* NL Visual Assertion Result */}
        {!isUser && message.nlVisualResult && (
          <NLVisualAssertionResult result={message.nlVisualResult} />
        )}

        {/* NL Exploration Result */}
        {!isUser && message.nlExplorationResult && (
          <NLExplorationResult result={message.nlExplorationResult} />
        )}

        {/* Site Analysis Dashboard */}
        {!isUser && message.siteAnalysis && (
          <SiteAnalysisDashboard progress={message.siteAnalysis} />
        )}

        {/* Generated Code Viewer */}
        {!isUser && message.generatedCode && (
          <GeneratedCodeViewer code={message.generatedCode} />
        )}

        {/* Codebase Analysis Viewer */}
        {!isUser && message.codebaseSnapshot && (
          <CodebaseAnalysisViewer snapshot={message.codebaseSnapshot} />
        )}

        {/* Codebase Test Results */}
        {!isUser && message.codebaseTestResults && (
          <CodebaseTestResultsCard results={message.codebaseTestResults} />
        )}

        {/* TMS Context Gathering Card */}
        {!isUser && message.tmsContextPrompt && onTMSContextSelect && (
          <TMSContextCard
            prompt={message.tmsContextPrompt}
            onSelect={(choice, planId, scope, testCaseIds) =>
              onTMSContextSelect(message.tmsContextPrompt!, choice, planId, scope, testCaseIds)
            }
            disabled={false}
          />
        )}

        {/* Slave Pre-flight Confirmation Card */}
        {!isUser && message.slavePreflight && onSlavePreflightConfirm && (
          <SlavePreflightCard
            data={message.slavePreflight}
            onConfirm={onSlavePreflightConfirm}
            projectId={preflightProjectId}
            canAuthorize={canAuthorizeDomain}
          />
        )}

        {/* Interactive Testing Session indicator */}
        {!isUser && message.testingSession && (
          <Chip
            label={`Interactive session active`}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}

        {/* Testing Event Card — inline event rendering */}
        {!isUser && message.testingEvent && (
          <TestingEventCard card={message.testingEvent} sessionId={activeTestingSessionId} />
        )}

        {/* Message Content */}
        {isUser ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
              '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 600 },
              '& h1': { fontSize: '1.25rem' },
              '& h2': { fontSize: '1.1rem' },
              '& h3': { fontSize: '1rem' },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& li': { mb: 0.5 },
              '& code': {
                bgcolor: 'grey.100',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.85em',
                fontFamily: 'monospace',
              },
              '& pre': {
                bgcolor: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                mb: 1,
                '& code': { bgcolor: 'transparent', p: 0 },
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                mb: 1,
                fontSize: '0.85rem',
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
                textAlign: 'left',
              },
              '& th': { bgcolor: 'grey.50', fontWeight: 600 },
              '& blockquote': {
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                color: 'text.secondary',
              },
              '& a': { color: 'primary.main' },
              '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 1.5 },
              wordBreak: 'break-word',
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </Box>
        )}

        {/* Tool Calls Display */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Tools Used:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {message.toolCalls.map((tool, idx) => (
                <Chip
                  key={idx}
                  size="small"
                  label={tool.tool}
                  color={tool.status === 'success' ? 'success' : tool.status === 'error' ? 'error' : 'default'}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Slave Agent Activity */}
        {message.slaveToolEvents && message.slaveToolEvents.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Accordion
              defaultExpanded={false}
              sx={{ bgcolor: 'grey.50', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Agent Activity ({message.slaveToolEvents.length} tool calls)
                  {message.slaveComplete && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      — {message.slaveComplete.num_turns} turns
                      {message.slaveComplete.duration_ms && `, ${(message.slaveComplete.duration_ms / 1000).toFixed(1)}s`}
                      {message.slaveComplete.cost_usd != null && ` · $${message.slaveComplete.cost_usd.toFixed(3)}`}
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1, maxHeight: 300, overflowY: 'auto' }}>
                {message.slaveToolEvents.map((evt, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      px: 1,
                      mb: 0.5,
                      borderLeft: '3px solid',
                      borderColor: evt.is_error ? 'error.main' : evt.type === 'slave_tool_start' ? 'primary.main' : 'success.main',
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                      {evt.tool}
                    </Typography>
                    {evt.input && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          maxHeight: 60,
                          overflow: 'hidden',
                        }}
                      >
                        {evt.input}
                      </Typography>
                    )}
                    {evt.output && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          color: evt.is_error ? 'error.main' : 'success.main',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          maxHeight: 60,
                          overflow: 'hidden',
                        }}
                      >
                        {evt.output}
                      </Typography>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Cost Summary Chip */}
        {!isUser && message.slaveComplete && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <UsageChip slaveComplete={message.slaveComplete} />
            {message.slaveComplete.num_turns != null && (
              <Chip
                size="small"
                label={`${message.slaveComplete.num_turns} turns`}
                variant="outlined"
                color="default"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
            {message.slaveComplete.duration_ms != null && (
              <Chip
                size="small"
                label={`${(message.slaveComplete.duration_ms / 1000).toFixed(1)}s`}
                variant="outlined"
                color="default"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Box>
        )}

        {/* Approval Request Card */}
        {message.approval && (
          message.nlTestPlan && message.approval.action?.includes('nl_author') ? (
            <NLTestApprovalCard
              approval={message.approval}
              nlPlan={message.nlTestPlan}
              onApprove={(id) => onApprove?.(id)}
              onReject={(id) => onReject?.(id)}
            />
          ) : (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: message.approval.risk_level === 'high' || message.approval.risk_level === 'critical'
                  ? 'error.main'
                  : 'warning.main',
                borderRadius: 2,
                bgcolor: message.approval.risk_level === 'high' || message.approval.risk_level === 'critical'
                  ? 'error.light'
                  : 'warning.light',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Action Requires Approval
                </Typography>
                {message.approval.risk_level && (
                  <Chip
                    size="small"
                    label={message.approval.risk_level.toUpperCase()}
                    color={
                      message.approval.risk_level === 'high' || message.approval.risk_level === 'critical'
                        ? 'error'
                        : 'warning'
                    }
                    variant="filled"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                )}
              </Box>

              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {message.approval.reason || message.approval.action}
              </Typography>

              {/* Show action details if available */}
              {message.approval.params && Object.keys(message.approval.params).length > 0 && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, fontSize: '0.8rem' }}>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                    Action Details:
                  </Typography>
                  {Object.entries(message.approval.params)
                    .filter(([key]) => !['conversation_id', 'user_id', 'session_cookie'].includes(key))
                    .slice(0, 6)
                    .map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        <strong>{key.replace(/_/g, ' ')}:</strong>{' '}
                        {typeof value === 'object' ? JSON.stringify(value).slice(0, 80) : String(value).slice(0, 80)}
                      </Typography>
                    ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => onApprove?.(message.approval!.approval_id)}
                  sx={{ fontWeight: 600 }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => onReject?.(message.approval!.approval_id)}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )
        )}

        {/* Coverage Dashboard */}
        {!isUser && message.coverageDashboard && (
          <CoverageDashboardCard
            tool={message.coverageDashboard.tool}
            data={message.coverageDashboard.data}
          />
        )}

        {/* Security Scan Results */}
        {message.securityScanResult && (
          <SecurityPanel scanResult={message.securityScanResult} />
        )}

        {/* Artifact Indicator */}
        {!isUser && artifactInfo?.hasArtifacts && message.status === 'complete' && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<LayersIcon sx={{ fontSize: '0.85rem !important' }} />}
              label={artifactInfo.types.length <= 2 ? artifactInfo.types.join(' · ') : `${artifactInfo.count} artifacts`}
              size="small"
              variant={isSelected ? 'filled' : 'outlined'}
              color={isSelected ? 'primary' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                onSelectMessage?.(message.id);
              }}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
                '& .MuiChip-icon': { ml: 0.5 },
                cursor: 'pointer',
              }}
            />
          </Box>
        )}

        {/* Copy Button */}
        {!isUser && message.status === 'complete' && (
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0.6,
              '&:hover': { opacity: 1 },
            }}
          >
            {copied ? <SuccessIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            opacity: 0.7,
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {message.timestamp.toLocaleTimeString()}
        </Typography>

        {!isUser && message.status === 'complete' && message.savedMessageId && (
          <MessageFeedback messageId={message.savedMessageId} />
        )}
      </Paper>
    </Box>
  );
};

// Welcome message component for new chats
const WelcomeMessage: React.FC<{ onQuickAction: (prompt: string) => void }> = ({ onQuickAction }) => (
  <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
    <AgentIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
    <Typography variant="h5" gutterBottom fontWeight={600}>
      How can I help you today?
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
      I'm your QA AI Agent. I can help with bug management, test coverage analysis,
      test planning, reporting, and automation maintenance.
    </Typography>

    {/* Quick Action Cards */}
    <Grid container spacing={2} sx={{ maxWidth: 600, mx: 'auto' }}>
      {QUICK_ACTIONS.slice(0, 4).map((category) => (
        <Grid item xs={12} sm={6} key={category.category}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
              },
            }}
            onClick={() => onQuickAction(category.prompts[0])}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {category.category}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {category.prompts[0]}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Full-screen project selection overlay — like Claude Code's repo selector.
// Blocks the entire UI until user picks a project.
const ProjectSelectionOverlay: React.FC<{
  projects: Project[];
  loading: boolean;
  onSelectProject: (project: Project) => void;
}> = ({ projects, loading, onSelectProject }) => {
  const [search, setSearch] = useState('');

  const filtered = projects.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.key.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ textAlign: 'center', px: 3, maxWidth: 520, width: '100%' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <AgentIcon sx={{ fontSize: 28, color: 'white' }} />
        </Box>
        <Typography variant="h5" gutterBottom fontWeight={700}>
          QA AI Agent
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a project to scope your conversation
        </Typography>

        {loading ? (
          <Box sx={{ py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading projects...
            </Typography>
          </Box>
        ) : projects.length === 0 ? (
          <Alert severity="info" sx={{ mx: 'auto' }}>
            No projects found. Contact your admin to create a project.
          </Alert>
        ) : (
          <>
            {/* Search input */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />

            {/* Scrollable project list */}
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                borderRadius: 2,
                textAlign: 'left',
              }}
            >
              {filtered.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No projects match "{search}"
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filtered.map((project, idx) => (
                    <React.Fragment key={project.id}>
                      {idx > 0 && <Divider />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => onSelectProject(project)}
                          sx={{ py: 1.5, px: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={project.key}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, minWidth: 40 }}
                                />
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {project.name}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              project.description ? (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25, pl: 0.5 }}>
                                  {project.description}
                                </Typography>
                              ) : undefined
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {filtered.length} of {projects.length} projects
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ChatPage: React.FC = () => {
  const { user, logout } = useAuth();

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState<'unknown' | 'online' | 'offline'>('unknown');
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sessionRefreshTrigger, setSessionRefreshTrigger] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [walletNotice, setWalletNotice] = useState<StreamNotice | null>(null);

  // Per-message artifact selection
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // Whether the current user is an admin of their current org — controls
  // whether the pre-flight card lets them authorize a new domain inline.
  const canAuthorizeDomain = useMemo(() => {
    if (!user) return false;
    const org =
      (user.current_organization_id
        ? user.organizations?.find((o) => o.organization_id === user.current_organization_id)
        : undefined) || user.organizations?.[0];
    const role = (org?.role || '').toLowerCase();
    return ['owner', 'admin', 'org_admin', 'administrator'].includes(role);
  }, [user]);

  // Project context state
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  // Session config state for interactive testing
  const [sessionFramework, setSessionFramework] = useState<'playwright' | 'selenium'>('playwright');
  const [sessionTmsTestCaseId, setSessionTmsTestCaseId] = useState('');
  const [sessionPlatform, setSessionPlatform] = useState<'linux' | 'android'>('linux');
  const [sessionDeviceName, setSessionDeviceName] = useState('');
  const [sessionAndroidVersion, setSessionAndroidVersion] = useState('14.0');
  const [sessionAppUrl, setSessionAppUrl] = useState('');
  const [showSessionConfig, setShowSessionConfig] = useState(false);
  const [showLoginConfig, setShowLoginConfig] = useState(false);
  // Test credential & OTP config
  const [sessionTestPhone, setSessionTestPhone] = useState('');
  const [sessionTestEmail, setSessionTestEmail] = useState('');
  const [sessionTestPassword, setSessionTestPassword] = useState('');
  const [sessionOtpProvider, setSessionOtpProvider] = useState<'manual' | 'android_sms' | 'fixed'>('manual');
  const [sessionOtpAndroidDevice, setSessionOtpAndroidDevice] = useState('');
  const [sessionOtpFixedCode, setSessionOtpFixedCode] = useState('');

  // Tools dialog state
  const [showToolsDialog, setShowToolsDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Active testing session — persisted to localStorage so page refresh reconnects.
  const [activeTestingSession, setActiveTestingSession] = useState<
    import('../types').TestingSessionCreatedEvent | null
  >(() => {
    // Restore from localStorage on mount
    try {
      const stored = localStorage.getItem('qa_agent_active_testing_session');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Track whether a session just completed (for refinement hint)
  const [sessionJustCompleted, setSessionJustCompleted] = useState(false);

  // Test plan draft state — right-panel editor
  const [activeTestPlanDraft, setActiveTestPlanDraft] = useState<TestPlanDraft | null>(null);
  const [isTestPlanSaving, setIsTestPlanSaving] = useState(false);

  // Test plan detail panel — shown when agent returns list_test_plans / get_test_plan results
  const [testPlanDetailItems, setTestPlanDetailItems] = useState<any[] | null>(null);
  const [testPlanDetailSelected, setTestPlanDetailSelected] = useState<any | null>(null);

  // Slave-delegate API execution tracking (right panel)
  const [slaveApiResults, setSlaveApiResults] = useState<APIStepResult[]>([]);
  const [slavePhase, setSlavePhase] = useState<string>('');
  const [slaveGeneratedCode, setSlaveGeneratedCode] = useState<GeneratedCodePayload | null>(null);
  const [slaveTmsIntegration, setSlaveTmsIntegration] = useState<TMSIntegrationInfo | null>(null);
  const [slaveDiscoveredEndpoints, setSlaveDiscoveredEndpoints] = useState<SlavePreflightEndpoint[]>([]);
  const [slaveFramework, setSlaveFramework] = useState<string>('');
  const [slaveLiveStream, setSlaveLiveStream] = useState<{
    sessionId: string;
    liveStreamUrl: string;
    vncUrl: string;
    livekitToken?: string;
    livekitUrl?: string;
    livekitRoom?: string;
  } | null>(null);
  const [agentHitlGate, setAgentHitlGate] = useState<import('types').AgentHitlGate | null>(null);
  const agentHitlToolRef = useRef('');
  // tool_use_ids being watched; request ids already shown; the card now open.
  const loadWatchRef = useRef<Set<string>>(new Set());
  const loadClaimedRef = useRef<Set<string>>(new Set());
  const loadOpenRequestRef = useRef('');
  useEffect(() => {
    agentHitlToolRef.current = agentHitlGate?.toolUseId || '';
    if (agentHitlGate?.eventType !== 'load_large') loadOpenRequestRef.current = '';
  }, [agentHitlGate]);

  // Open the HITL gate for a slave_tool_start of a HITL tool. For tools whose
  // prompt the server composes (security_create_issue), load the pending
  // request (message, findings, request_id) before approve/deny is enabled.
  // Capped load tools (load_run / load_upsert_test / load_schedule) open no
  // gate when they start. If the server registers a `load_large` request
  // (the call is over 25 users or 120 s), show its approve / decline card.
  const watchLoadLarge = (
    event: { tool?: string; tool_use_id?: string },
    sessionId: string,
  ) => {
    const toolUseId = event.tool_use_id || '';
    const toolKey = loadCapToolKey(event.tool || '');
    // Two event paths report the same tool start: watch each call once.
    if (!sessionId || !toolUseId || !toolKey || loadWatchRef.current.has(toolUseId)) return;
    loadWatchRef.current.add(toolUseId);
    void (async () => {
      // Requests already pending are stale (an earlier call): never answer them.
      const ignore = new Set<string>(loadClaimedRef.current);
      try {
        (await getPendingHitlRequests(sessionId)).forEach((r) => {
          if (r.event_type === 'load_large') ignore.add(r.request_id);
        });
      } catch {
        /* the poll below retries */
      }
      const pending = await waitForPendingHitl(() => getPendingHitlRequests(sessionId), 'load_large', {
        attempts: 150,
        intervalMs: 2000,
        isCancelled: () => !loadWatchRef.current.has(toolUseId),
        // One card at a time; a parallel call's card waits for its turn.
        match: (r) =>
          !loadOpenRequestRef.current && isOwnLoadLargeRequest(r, toolKey, ignore),
      });
      if (!pending || !loadWatchRef.current.has(toolUseId)) return;
      loadClaimedRef.current.add(pending.request_id);
      loadOpenRequestRef.current = pending.request_id;
      agentHitlToolRef.current = toolUseId;
      setAgentHitlGate(loadLargeGate(pending, sessionId, toolUseId));
    })();
  };

  const openAgentHitlGate = (
    event: { tool?: string; tool_use_id?: string; input?: string },
    sessionId: string,
  ) => {
    const gate = hitlGateFromToolStart(event, sessionId);
    if (!gate) return;
    agentHitlToolRef.current = gate.toolUseId;
    setAgentHitlGate(gate);
    if (!isServerComposedHitlTool(event.tool || '') || !sessionId) return;
    void waitForPendingHitl(() => getPendingHitlRequests(sessionId), gate.eventType, {
      isCancelled: () => agentHitlToolRef.current !== gate.toolUseId,
    }).then((pending) => {
      setAgentHitlGate((prev) => {
        if (!prev || prev.toolUseId !== gate.toolUseId) return prev;
        if (!pending) {
          return { ...prev, message: 'The approval request could not be loaded.', data: { unavailable: true } };
        }
        return { ...prev, requestId: pending.request_id, message: pending.message, data: pending.data || {} };
      });
    });
  };

  // Derive session from latest message when available (new session created)
  const messageSession = messages
    .slice()
    .reverse()
    .find((m) => m.role === 'assistant' && m.testingSession)?.testingSession || null;

  // Sync: when a new session appears in messages, persist it
  useEffect(() => {
    if (messageSession && messageSession.session_id !== activeTestingSession?.session_id) {
      setActiveTestingSession(messageSession);
      localStorage.setItem('qa_agent_active_testing_session', JSON.stringify(messageSession));
    }
  }, [messageSession?.session_id]);

  // On mount: validate stored session is still alive, clear if not
  useEffect(() => {
    if (!activeTestingSession) return;
    // Skip validation if session came from current messages
    if (messageSession?.session_id === activeTestingSession.session_id) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await testingService.getStatus(activeTestingSession.session_id);
        if (cancelled) return;
        const phase = status?.phase;
        if (phase === 'completed' || phase === 'failed' || phase === 'cancelled') {
          // Session is done — clear it
          setActiveTestingSession(null);
          localStorage.removeItem('qa_agent_active_testing_session');
        }
      } catch {
        if (!cancelled) {
          // Session not found on backend — clear it
          setActiveTestingSession(null);
          localStorage.removeItem('qa_agent_active_testing_session');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resizable split pane state (percentage for chat width when testing session is active)
  const [chatWidthPercent, setChatWidthPercent] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  // Persist provision_browser data in a ref as ultimate fallback —
  // refs update synchronously and survive React batching/timing issues.
  const provisionBrowserRef = useRef<{
    sessionId: string;
    liveStreamUrl: string;
    vncUrl: string;
    livekitToken?: string;
    livekitUrl?: string;
    livekitRoom?: string;
  } | null>(null);

  // Testing event callback — appends testing events as inline chat messages
  const handleTestingEvent = useCallback((msg: Partial<ChatMessage>) => {
    const chatMsg: ChatMessage = {
      id: `testing-event-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: msg.role || 'assistant',
      content: msg.content || '',
      timestamp: new Date(),
      status: 'complete',
      testingEvent: msg.testingEvent,
    };
    setMessages((prev) => [...prev, chatMsg]);
  }, []);

  // useTestingSession — hoisted to ChatPage level (previously inside InteractiveTestingPanel)
  const testingSession = useTestingSession(
    activeTestingSession?.session_id || null,
    handleTestingEvent,
  );

  // When testing session reaches terminal phase, mark completed and clear active session
  useEffect(() => {
    const phase = testingSession.phase;
    if (phase === 'completed' || phase === 'failed' || phase === 'cancelled') {
      setSessionJustCompleted(phase === 'completed');
      // Don't clear activeTestingSession immediately — keep browser pane open
      // until user dismisses it or starts a new session
    }
  }, [testingSession.phase]);

  // Derive AgentToolActivity[] from the current assistant message's slaveToolEvents
  const slaveAgentToolEvents: AgentToolActivity[] = useMemo(() => {
    const currentMsg = [...messages].reverse().find(
      (m) => m.role === 'assistant' && m.slaveToolEvents && m.slaveToolEvents.length > 0
    );
    if (!currentMsg?.slaveToolEvents) return [];
    return currentMsg.slaveToolEvents.map((evt) => ({
      task: 'API Testing',
      tool: evt.tool,
      tool_use_id: evt.tool_use_id || '',
      input: evt.input,
      output: evt.output,
      is_error: evt.is_error,
      phase: evt.type === 'slave_tool_start' ? ('start' as const) : ('end' as const),
    }));
  }, [messages]);

  // Per-message artifact selection: toggle handler
  // Disabled when a live session panel (Mode A) is active to prevent the right
  // panel from toggling away while a test is in progress or results are shown.
  const hasActiveSessionPanel = !!(
    activeTestPlanDraft ||
    (testPlanDetailItems && testPlanDetailItems.length > 0) ||
    (slaveLiveStream?.liveStreamUrl || slaveLiveStream?.vncUrl || slaveLiveStream?.livekitToken) ||
    slaveApiResults.length > 0 ||
    slaveGeneratedCode ||
    (activeTestingSession && (testingSession.liveStreamUrl || testingSession.apiStepResults.length > 0 || testingSession.isPageSelectionPending || !!testingSession.phase))
  );

  const handleSelectMessage = useCallback((messageId: string) => {
    if (hasActiveSessionPanel) return;
    setSelectedMessageId((prev) => (prev === messageId ? null : messageId));
  }, [hasActiveSessionPanel]);

  // Derive the selected message and its panel data (Mode B)
  const selectedMessage = selectedMessageId ? messages.find((m) => m.id === selectedMessageId) : null;

  // When a message is selected (Mode B), extract right-panel data from it
  const selectedPanelData = useMemo(() => {
    if (!selectedMessage) return null;

    // Extract API results from slaveToolEvents (same logic as session hydration)
    const apiResults: APIStepResult[] = [];
    let detectedFramework = '';
    let detectedGeneratedCode: GeneratedCodePayload | null = null;

    if (selectedMessage.slaveToolEvents?.length) {
      for (const evt of selectedMessage.slaveToolEvents) {
        if (evt.type === 'slave_tool_start' && evt.tool?.includes('execute_api_request')) {
          let reqMethod = 'GET';
          let reqUrl = '';
          try {
            const inputJson = JSON.parse(evt.input || '{}');
            reqMethod = (inputJson.method || 'GET').toUpperCase();
            reqUrl = inputJson.url || inputJson.endpoint || '';
          } catch { /* ignore */ }
          apiResults.push({
            step_index: apiResults.length,
            action_id: evt.tool_use_id || `api-${apiResults.length}`,
            status: 'running',
            source: 'api_probe',
            description: `${reqMethod} ${reqUrl}`,
            request: { method: reqMethod, url: reqUrl, headers: {} },
          });
        } else if (evt.type === 'slave_tool_end' && evt.api_result) {
          const lastRunning = [...apiResults].reverse().find((r) => r.status === 'running');
          if (lastRunning) {
            lastRunning.status = evt.is_error ? 'failed' : (evt.api_result.status || 'passed');
            lastRunning.duration_ms = evt.api_result.duration_ms;
            if (evt.api_result.response) {
              lastRunning.response = evt.api_result.response as APIResponseDetails;
            }
          }
        }

        // Extract test execution results from execute_pytest tool_end events
        if (evt.type === 'slave_tool_end' && evt.tool?.includes('execute_pytest') && evt.output && !evt.is_error) {
          try {
            const execResult = JSON.parse(evt.output);
            if (execResult.results?.length) {
              const fw: string = execResult.framework || '';
              const isUIFw = fw === 'playwright' || fw === 'selenium';
              if (fw) detectedFramework = fw;

              const testResults: APIStepResult[] = execResult.results.map((r: any, i: number) => {
                const nodeid: string = r.name || `Test ${i + 1}`;
                const funcName = nodeid.includes('::') ? nodeid.split('::').pop() || nodeid : nodeid;
                let category: 'functional' | 'negative' | 'security' = 'functional';
                const lowerNodeid = nodeid.toLowerCase();
                if (lowerNodeid.includes('security') || lowerNodeid.includes('injection') || lowerNodeid.includes('xss')) {
                  category = 'security';
                } else if (lowerNodeid.includes('not_found') || lowerNodeid.includes('invalid') || lowerNodeid.includes('unauthorized') || lowerNodeid.includes('forbidden') || lowerNodeid.includes('error') || lowerNodeid.includes('negative')) {
                  category = 'negative';
                }

                if (isUIFw) {
                  return {
                    step_index: i,
                    action_id: `test-${i}`,
                    status: r.status === 'passed' ? 'passed' : (r.status === 'skipped' ? 'skipped' : 'failed'),
                    source: 'test_execution' as const,
                    description: nodeid,
                    duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
                    error: r.error_message || r.longrepr || r.error,
                    category,
                  };
                }

                const httpCalls: any[] = r.http_calls || [];
                const representativeCall = r.status !== 'passed' && httpCalls.length > 0
                  ? httpCalls[httpCalls.length - 1]
                  : httpCalls[0];

                const inferMethod = (name: string): string => {
                  const lower = name.toLowerCase();
                  if (lower.includes('delete')) return 'DELETE';
                  if (lower.includes('create') || lower.includes('post') || lower.includes('register') || lower.includes('signup') || lower.includes('login')) return 'POST';
                  if (lower.includes('update') || lower.includes('patch')) return 'PATCH';
                  if (lower.includes('put')) return 'PUT';
                  return 'GET';
                };

                return {
                  step_index: i,
                  action_id: `test-${i}`,
                  status: r.status === 'passed' ? 'passed' : (r.status === 'skipped' ? 'skipped' : 'failed'),
                  source: 'test_execution' as const,
                  description: nodeid,
                  duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
                  error: r.error_message || r.longrepr || r.error,
                  category,
                  request: representativeCall ? {
                    method: representativeCall.method,
                    url: representativeCall.url,
                    headers: representativeCall.request?.headers || {},
                  } : {
                    method: inferMethod(funcName),
                    url: funcName,
                    headers: {},
                  },
                  response: representativeCall?.status_code ? {
                    status_code: representativeCall.status_code,
                    headers: representativeCall.response?.headers || {},
                    body: '',
                    content_type: representativeCall.response?.headers?.['content-type'] || '',
                    duration_ms: representativeCall.duration_ms || 0,
                  } : undefined,
                };
              });
              apiResults.push(...testResults);
            }
          } catch { /* ignore parse errors */ }
        }

        // Extract generated code from save_test_artifacts tool_end events
        if (evt.type === 'slave_tool_end' && evt.tool?.includes('save_test_artifacts') && evt.output && !evt.is_error) {
          try {
            const artifactResult = JSON.parse(evt.output);
            if (artifactResult.files) {
              detectedGeneratedCode = {
                files: artifactResult.files.map((f: any) => ({
                  path: f.path,
                  content: f.content,
                  language: 'python',
                  framework: artifactResult.framework || 'pytest',
                })),
                summary: {
                  total_files: artifactResult.files.length,
                  total_test_cases: artifactResult.test_count || 0,
                  frameworks_used: [artifactResult.framework || 'pytest'],
                },
              };
            }
          } catch { /* ignore parse errors */ }
        }
      }
    }

    // Derive agent tool events
    const agentTools: AgentToolActivity[] = (selectedMessage.slaveToolEvents || []).map((evt) => ({
      task: detectedFramework === 'selenium' || detectedFramework === 'playwright' ? 'UI Testing' : 'API Testing',
      tool: evt.tool,
      tool_use_id: evt.tool_use_id || '',
      input: evt.input,
      output: evt.output,
      is_error: evt.is_error,
      phase: evt.type === 'slave_tool_start' ? ('start' as const) : ('end' as const),
    }));

    const effectiveGeneratedCode = selectedMessage.generatedCode || detectedGeneratedCode || null;

    return {
      apiResults,
      generatedCode: effectiveGeneratedCode,
      sqlData: selectedMessage.sqlData || null,
      testPlanDraft: selectedMessage.testPlanDraft || null,
      securityScanResult: selectedMessage.securityScanResult || null,
      slaveComplete: selectedMessage.slaveComplete || null,
      phase: selectedMessage.slaveComplete ? 'completed' : (apiResults.length > 0 ? 'executing' : ''),
      framework: detectedFramework || undefined,
      agentToolEvents: agentTools,
    };
  }, [selectedMessage]);

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    checkAgentStatus();
    loadAvailableProjects();
  }, []);

  const loadAvailableProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await listProjects();
      setAvailableProjects(response.projects);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Backup: scan message data for provision_browser results and set
  // slaveLiveStream if the SSE callback failed to set it.  This runs
  // on every messages update but short-circuits immediately if
  // slaveLiveStream is already set.
  useEffect(() => {
    if (slaveLiveStream) return;

    // Scan every message newest-first, not just the last one.
    //
    // provision_browser fires once, near the start of a run, and its event is
    // attached to whichever assistant message was streaming at that moment.
    // Everything after it — further agent narration, the page-selection gate,
    // a follow-up user turn — appends newer messages, so within seconds the
    // provisioning event is no longer in messages[length - 1] and this backup
    // could never fire. The same applied to session reload: hydration attaches
    // the events to the last *assistant* message, which is not the last message
    // whenever the transcript ends on a user turn.
    for (let i = messages.length - 1; i >= 0; i--) {
      const events = messages[i]?.slaveToolEvents;
      if (!events?.length) continue;

      for (const evt of events) {
        if (
          evt.type === 'slave_tool_end' &&
          evt.tool?.includes('provision_browser') &&
          evt.output &&
          !evt.is_error
        ) {
          try {
            const result = JSON.parse(evt.output);
            if (result.vnc_url || result.live_stream_url || result.livekit_token) {
              setSlaveLiveStream({
                sessionId: result.session_id || '',
                liveStreamUrl: result.live_stream_url || '',
                vncUrl: result.vnc_url || '',
                livekitToken: result.livekit_token || undefined,
                livekitUrl: result.livekit_url || undefined,
                livekitRoom: result.livekit_room || undefined,
              });
              setSlaveFramework('selenium');
              if (!slavePhase) setSlavePhase('executing');
              return;
            }
          } catch { /* ignore parse errors */ }
        }
      }
    }
  }, [messages, slaveLiveStream, slavePhase]);

  // ============================================================================
  // Functions
  // ============================================================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Right panel visibility — two modes:
  // Mode A (no message selected): session-level state drives the panel (streaming behavior)
  // Mode B (message selected): selected message's artifact fields drive the panel
  const isSelectedMessageMode = !!selectedPanelData;
  const selectedHasApiPanel = isSelectedMessageMode && (selectedPanelData!.apiResults.length > 0 || !!selectedPanelData!.generatedCode);
  const selectedHasTestPlanDraft = isSelectedMessageMode && !!selectedPanelData!.testPlanDraft;

  // Mode A panel flags (session-level)
  const isTestPlanPanel = !isSelectedMessageMode && !!activeTestPlanDraft;
  const isTestPlanDetailPanel = !isSelectedMessageMode && !isTestPlanPanel && !!testPlanDetailItems && testPlanDetailItems.length > 0;
  // Check both React state AND the ref fallback for live stream data.
  // The ref updates synchronously and survives React batching issues.
  const effectiveSlaveLiveStream = slaveLiveStream || provisionBrowserRef.current;
  const hasSlaveLiveStream = !isSelectedMessageMode && !!(effectiveSlaveLiveStream?.liveStreamUrl || effectiveSlaveLiveStream?.vncUrl || effectiveSlaveLiveStream?.livekitToken);
  const hasSlaveApiPanel = !isSelectedMessageMode && (slaveApiResults.length > 0 || !!slaveGeneratedCode);

  // Open the panel because a browser test is RUNNING, not because we managed
  // to parse a stream URL out of one tool result.
  //
  // Every gate above depends on that parse succeeding. It happens inside a
  // try/catch that swallows failures, so a single unexpected payload took the
  // entire right-hand side of the screen with it — no live view, no progress,
  // no results, nothing to indicate a test was even running, while the agent
  // cheerfully narrated "Browser provisioned with live view".
  //
  // UITestingPanel already has a pre-stream placeholder for exactly this
  // state, so showing it early costs nothing and the live view fills itself
  // in when the URL does arrive.
  const hasSlaveUiActivity = useMemo(() => {
    // Browser-specific tools only. execute_pytest is deliberately NOT here:
    // API testing (pytest + httpx, no browser) runs execute_pytest too, so
    // including it opened the browser live-view panel for API runs and
    // labelled an API test "UI Test Execution" — the exact strategy-mixing
    // the user flagged. A UI session is defined by a browser being
    // provisioned, not by pytest running.
    const BROWSER_TOOLS = ['provision_browser', 'crawl_site', 'authenticate_session'];
    for (let i = messages.length - 1; i >= 0; i--) {
      const events = messages[i]?.slaveToolEvents;
      if (!events?.length) continue;
      if (events.some((e) => BROWSER_TOOLS.some((t) => e.tool?.includes(t)))) return true;
    }
    return false;
  }, [messages]);
  const hasSlaveSession = !isSelectedMessageMode && hasSlaveUiActivity;

  const hasSessionRightPanel = isTestPlanPanel || isTestPlanDetailPanel || hasSlaveLiveStream || hasSlaveApiPanel || hasSlaveSession || !!(activeTestingSession && (testingSession.liveStreamUrl || testingSession.apiStepResults.length > 0 || testingSession.isPageSelectionPending || testingSession.isTestCaseReviewPending || testingSession.isCodeReviewPending || !!testingSession.phase));
  const hasRightPanel = hasSessionRightPanel || selectedHasApiPanel || selectedHasTestPlanDraft;
  const isSlaveLiveStreamPanel = !isTestPlanPanel && !isTestPlanDetailPanel && !isSelectedMessageMode && (hasSlaveLiveStream || hasSlaveSession);
  const isApiPanel = !isTestPlanPanel && !isTestPlanDetailPanel && !isSlaveLiveStreamPanel && !isSelectedMessageMode && (hasSlaveApiPanel || (hasSessionRightPanel && !testingSession.liveStreamUrl && !testingSession.isPageSelectionPending && testingSession.apiStepResults.length > 0));
  const isPageSelectionPanel = !isTestPlanPanel && !isTestPlanDetailPanel && !hasSlaveApiPanel && !isSlaveLiveStreamPanel && !isSelectedMessageMode && hasSessionRightPanel && testingSession.isPageSelectionPending;

  // Mode B panel flags (selected message)
  const isSelectedApiPanel = isSelectedMessageMode && selectedHasApiPanel;
  const isSelectedTestPlanPanel = isSelectedMessageMode && selectedHasTestPlanDraft && !selectedHasApiPanel;

  // Resizable split pane drag handlers
  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const pct = Math.round((x / rect.width) * 100);
      // Clamp between 20% and 60%
      setChatWidthPercent(Math.min(60, Math.max(20, pct)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const checkAgentStatus = async () => {
    try {
      await apiService.getHealthStatus();
      setAgentStatus('online');
    } catch (e) {
      setAgentStatus('offline');
    }
  };

  const handleNewChat = async () => {
    // Abort any active SSE stream from the previous session
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    // Reset slave API panel state
    setSlaveApiResults([]);
    setSlavePhase('');
    setSlaveGeneratedCode(null);
    setSlaveFramework('');
    setSlaveLiveStream(null);
    provisionBrowserRef.current = null;
    // Reset test plan detail panel
    setTestPlanDetailItems(null);
    setTestPlanDetailSelected(null);
    // Reset per-message selection
    setSelectedMessageId(null);
    setWalletNotice(null);
    // No server session yet: it is created when the first message is sent
    // (see sendMessage), so abandoned "New Chat" clicks no longer pile up as
    // empty "New Session · 0 messages" entries in the history.
    setCurrentSessionId(null);
    setMessages([]);
    setError(null);
    inputRef.current?.focus();
  };

  const handleSelectSession = async (session: ChatSession) => {
    // Abort any active SSE stream from the previous session
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    // Reset slave API panel when switching sessions
    setSlaveApiResults([]);
    setSlavePhase('');
    setSlaveGeneratedCode(null);
    setSlaveFramework('');
    setSlaveLiveStream(null);
    provisionBrowserRef.current = null;
    // Reset per-message selection
    setSelectedMessageId(null);
    try {
      const sessionWithMessages = await sessionService.getSession(session.id);
      setCurrentSessionId(session.id);

      // Session-level artifacts from the dedicated table
      const artifacts = sessionWithMessages.artifacts || {};

      // Convert session messages to ChatMessage format
      const loadedMessages: ChatMessage[] = sessionWithMessages.messages.map((msg) => ({
        id: msg.id,
        savedMessageId: msg.role === 'assistant' ? msg.id : undefined,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        status: 'complete' as const,
        toolCalls: msg.tool_calls
          ? Object.entries(msg.tool_calls).map(([tool, result]) => ({
              tool,
              status: 'success' as const,
              result,
            }))
          : undefined,
      }));

      // Hydrate artifact fields onto the last assistant message for rendering
      const lastAssistantIdx = loadedMessages.map(m => m.role).lastIndexOf('assistant');
      if (lastAssistantIdx >= 0) {
        const lastAssistant = loadedMessages[lastAssistantIdx];
        // Generated code always goes to the right panel (APIExecutionPanel →
        // Code Repository tab) via setSlaveGeneratedCode below. We no longer
        // hydrate it inline on the message to avoid duplicate display.
        lastAssistant.sqlData = artifacts.sql_data;
        lastAssistant.slaveToolEvents = artifacts.slave_tool_events;
        lastAssistant.slaveComplete = artifacts.slave_complete;
        lastAssistant.nlTestPlan = artifacts.nl_test_plan;
        lastAssistant.nlExecutionProgress = artifacts.nl_execution_progress as any;
        lastAssistant.nlVisualResult = artifacts.nl_visual_result as any;
        lastAssistant.nlExplorationResult = artifacts.nl_exploration as any;
        lastAssistant.siteAnalysis = artifacts.site_analysis;
        lastAssistant.testingSession = artifacts.testing_session;
        lastAssistant.testPlanDraft = artifacts.test_plan_draft;
        lastAssistant.slavePreflight = artifacts.slave_preflight;
        lastAssistant.securityScanResult = artifacts.security_scan_result;

        // --- Restore panel-level state from session artifacts ---

        // 1. API probe results from slave_tool_events (direct execute_api_request calls)
        const apiResults: APIStepResult[] = [];
        if (lastAssistant.slaveToolEvents?.length) {
          for (const evt of lastAssistant.slaveToolEvents) {
            if (evt.type === 'slave_tool_start' && evt.tool?.includes('execute_api_request')) {
              let reqMethod = 'GET';
              let reqUrl = '';
              try {
                const inputJson = JSON.parse(evt.input || '{}');
                reqMethod = (inputJson.method || 'GET').toUpperCase();
                reqUrl = inputJson.url || inputJson.endpoint || '';
              } catch { /* ignore */ }
              apiResults.push({
                step_index: apiResults.length,
                action_id: evt.tool_use_id || `api-${apiResults.length}`,
                status: 'running',
                source: 'api_probe',
                description: `${reqMethod} ${reqUrl}`,
                request: { method: reqMethod, url: reqUrl, headers: {} },
              });
            } else if (evt.type === 'slave_tool_end' && evt.api_result) {
              const lastRunning = [...apiResults].reverse().find((r) => r.status === 'running');
              if (lastRunning) {
                lastRunning.status = evt.is_error ? 'failed' : (evt.api_result.status || 'passed');
                lastRunning.duration_ms = evt.api_result.duration_ms;
                if (evt.api_result.response) {
                  lastRunning.response = evt.api_result.response as APIResponseDetails;
                }
              }
            }
          }
        }

        // 2. Test execution results from derived artifact (execute_pytest output)
        const testExecResults = artifacts.test_execution_results;
        if (testExecResults?.results?.length) {
          const fw: string = testExecResults.framework || '';
          const isUIFramework = fw === 'playwright' || fw === 'selenium';
          if (fw) setSlaveFramework(fw);

          const testResults: APIStepResult[] = testExecResults.results.map((r: any, i: number) => {
            const nodeid: string = r.name || `Test ${i + 1}`;
            // Extract short function name from nodeid like "tests/test_admin.py::test_create_exam"
            const funcName = nodeid.includes('::') ? nodeid.split('::').pop() || nodeid : nodeid;
            let category: 'functional' | 'negative' | 'security' = 'functional';
            const lowerNodeid = nodeid.toLowerCase();
            if (lowerNodeid.includes('security') || lowerNodeid.includes('injection') || lowerNodeid.includes('xss')) {
              category = 'security';
            } else if (lowerNodeid.includes('not_found') || lowerNodeid.includes('invalid') || lowerNodeid.includes('unauthorized') || lowerNodeid.includes('forbidden') || lowerNodeid.includes('error') || lowerNodeid.includes('negative')) {
              category = 'negative';
            }

            const httpCalls: any[] = r.http_calls || [];
            const representativeCall = r.status !== 'passed' && httpCalls.length > 0
              ? httpCalls[httpCalls.length - 1]
              : httpCalls[0];

            // For UI tests (playwright/selenium), don't infer HTTP methods or create fake request objects
            if (isUIFramework) {
              return {
                step_index: i,
                action_id: `test-${i}`,
                status: r.status === 'passed' ? 'passed' : (r.status === 'skipped' ? 'skipped' : 'failed'),
                source: 'test_execution' as const,
                description: nodeid,
                duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
                error: r.error,
                category,
              };
            }

            // Infer HTTP method from test function name when no http_calls (API tests only)
            const inferMethod = (name: string): string => {
              const lower = name.toLowerCase();
              if (lower.includes('delete')) return 'DELETE';
              if (lower.includes('create') || lower.includes('post') || lower.includes('register') || lower.includes('signup') || lower.includes('login')) return 'POST';
              if (lower.includes('update') || lower.includes('patch')) return 'PATCH';
              if (lower.includes('put')) return 'PUT';
              return 'GET';
            };

            return {
              step_index: i,
              action_id: `test-${i}`,
              status: r.status === 'passed' ? 'passed' : (r.status === 'skipped' ? 'skipped' : 'failed'),
              source: 'test_execution' as const,
              description: nodeid,
              duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
              error: r.error,
              category,
              total_steps: httpCalls.length || undefined,
              http_calls: httpCalls.length > 0 ? httpCalls.map((hc: any) => ({
                method: hc.method || 'GET',
                url: hc.url || '',
                status_code: hc.status_code || 0,
                duration_ms: hc.duration_ms,
              })) : undefined,
              request: representativeCall ? {
                method: representativeCall.method,
                url: representativeCall.url,
                headers: representativeCall.request?.headers || {},
              } : {
                method: inferMethod(funcName),
                url: funcName,
                headers: {},
              },
              response: representativeCall?.status_code ? {
                status_code: representativeCall.status_code,
                headers: representativeCall.response?.headers || {},
                body: '',
                content_type: representativeCall.response?.headers?.['content-type'] || '',
                duration_ms: representativeCall.duration_ms || 0,
              } : undefined,
            };
          });
          // Merge: keep api_probe results, add test_execution results
          apiResults.push(...testResults);
        }

        if (apiResults.length > 0) {
          setSlaveApiResults(apiResults);
        }

        // 3. Generated code from derived artifact
        if (artifacts.generated_code) {
          setSlaveGeneratedCode(artifacts.generated_code);
        }

        // Set slave phase if any panel data was restored
        if (apiResults.length > 0 || artifacts.generated_code) {
          setSlavePhase(lastAssistant.slaveComplete ? 'completed' : 'executing');
        }

        // 4. TMS integration from derived artifact
        if (artifacts.tms_integration) {
          setSlaveTmsIntegration(artifacts.tms_integration as any);
        }
      }

      setMessages(loadedMessages);
      setError(null);

      // Auto-select the last assistant message if it has artifacts
      if (lastAssistantIdx >= 0) {
        const lastAssistant = loadedMessages[lastAssistantIdx];
        const info = getMessageArtifactInfo(lastAssistant);
        if (info.hasArtifacts) {
          setSelectedMessageId(lastAssistant.id);
        }
      }
    } catch (err: any) {
      console.error('Failed to load session:', err);
      setError('Failed to load chat session');
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    if (currentSessionId === sessionId) {
      setCurrentSessionId(null);
      setMessages([]);
    }
  };

  // Command routing for testing session — intercepts known commands
  const tryRouteCommand = useCallback((text: string): boolean => {
    if (!activeTestingSession) return false;
    const cmd = text.trim().toLowerCase();

    type CommandEntry = { patterns: string[]; action: () => void; response: string };
    const commands: CommandEntry[] = [
      { patterns: ['approve', 'approve steps'], action: () => testingSession.approve(), response: 'Steps approved. Executing...' },
      { patterns: ['reject', 'reject steps'], action: () => testingSession.reject(), response: 'Steps rejected. Regenerating...' },
      { patterns: ['skip login'], action: () => testingSession.skipLogin(), response: 'Login skipped.' },
      { patterns: ['stop', 'cancel', 'stop session'], action: () => testingSession.cancel(), response: 'Session cancelled.' },
      { patterns: ['end session'], action: () => testingSession.endSession(), response: 'Ending session...' },
      { patterns: ['solve captcha'], action: () => testingSession.solveCaptcha(), response: 'Solving CAPTCHA...' },
      { patterns: ['skip captcha'], action: () => testingSession.skipCaptcha(), response: 'CAPTCHA skipped.' },
      { patterns: ['retry', 're-execute', 'reexecute'], action: () => testingSession.approveReexecution(), response: 'Re-executing failed steps...' },
      { patterns: ['skip retry', 'skip reexecution'], action: () => testingSession.skipReexecution(), response: 'Skipped re-execution.' },
    ];

    // Sort by longest pattern first so "skip retry" matches before "retry"
    const sortedCommands = [...commands].sort((a, b) => {
      const maxA = Math.max(...a.patterns.map((p) => p.length));
      const maxB = Math.max(...b.patterns.map((p) => p.length));
      return maxB - maxA;
    });

    for (const entry of sortedCommands) {
      const matched = entry.patterns.some((pattern) => {
        if (cmd === pattern) return true;
        const escaped = pattern.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        return new RegExp(`\\b${escaped}\\b`, 'i').test(cmd);
      });
      if (matched) {
        entry.action();
        setMessages((prev) => [
          ...prev,
          {
            id: `cmd-response-${Date.now()}`,
            role: 'assistant',
            content: entry.response,
            timestamp: new Date(),
            status: 'complete',
          },
        ]);
        return true;
      }
    }

    // Local analytics: answer simple pass/fail summary questions
    // from apiStepResults without a backend round-trip.
    const results = testingSession.apiStepResults;
    if (results.length > 0) {
      const analyticsPatterns = [
        /how\s+many\b.*(?:pass|fail|succeed|error)/i,
        /(?:pass|fail)\s+(?:count|rate|summary|stats)/i,
        /(?:api|test|step)\s+(?:results?|summary|stats|status)/i,
        /(?:total|overall)\s+(?:pass|fail|results?)/i,
        /(?:what|which)\s+(?:apis?|calls?|tests?)\s+(?:pass|fail)/i,
      ];
      if (analyticsPatterns.some((p) => p.test(cmd))) {
        const passed = results.filter((r) => {
          const code = r.response?.status_code;
          return code != null && code >= 200 && code < 300;
        });
        const failed = results.filter((r) => {
          const code = r.response?.status_code;
          return r.status === 'failed' || (code != null && code >= 400);
        });
        const running = results.filter((r) => r.status === 'running');

        let summary = `**API Test Results** (${results.length} total steps)\n\n`;
        summary += `| Status | Count |\n|---|---|\n`;
        summary += `| Passed | ${passed.length} |\n`;
        summary += `| Failed | ${failed.length} |\n`;
        if (running.length > 0) summary += `| Running | ${running.length} |\n`;
        summary += `\n**Pass rate:** ${results.length > 0 ? Math.round((passed.length / results.length) * 100) : 0}%\n`;

        if (failed.length > 0) {
          summary += `\n**Failed API calls:**\n`;
          for (const f of failed.slice(0, 20)) {
            const method = f.request?.method || f.action_id || '?';
            const path = f.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '';
            const code = f.response?.status_code || '?';
            const err = f.error?.slice(0, 100) || f.description || '';
            summary += `- Step ${f.step_index + 1}: \`${method} ${path}\` -> ${code} ${err ? `(${err})` : ''}\n`;
          }
          if (failed.length > 20) summary += `- ... and ${failed.length - 20} more\n`;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `local-analytics-${Date.now()}`,
            role: 'assistant',
            content: summary,
            timestamp: new Date(),
            status: 'complete',
          },
        ]);
        return true;
      }
    }

    return false;
  }, [activeTestingSession, testingSession]);

  const sendMessage = useCallback(async (overrideMessage?: string, extraContext?: Record<string, any>) => {
    const messageText = overrideMessage || input.trim();
    if (!messageText || isLoading) return;

    // Clear the refinement hint after user sends a follow-up
    if (sessionJustCompleted) setSessionJustCompleted(false);
    // Clear per-message selection so right panel auto-follows the new stream
    setSelectedMessageId(null);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      status: 'complete',
    };

    // Check if this is a testing session command
    if (activeTestingSession && !overrideMessage) {
      const isCommand = tryRouteCommand(messageText);
      if (isCommand) {
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        return;
      }
    }

    // Don't add a duplicate user bubble for preflight confirmations —
    // the original user message is already in the chat.
    if (!extraContext?.slave_approval_id) {
      setMessages((prev) => [...prev, userMessage]);
    }
    if (!overrideMessage) setInput('');
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);

    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      status: 'streaming',
      toolCalls: [],
    };
    setMessages((prev) => [...prev, assistantMessage]);

    try {
      // Create an AbortController so session switches can cancel this stream
      const abortController = new AbortController();
      streamAbortRef.current = abortController;

      // Lazily create the chat session on the first message, so the history
      // only ever contains conversations that have at least one message.
      let conversationId = currentSessionId;
      if (!conversationId) {
        try {
          const session = await sessionService.createSession(undefined, currentProject?.id);
          conversationId = session.id;
          setCurrentSessionId(session.id);
        } catch (err) {
          console.error('Failed to create session:', err);
          // Still allow chatting — the backend creates a session itself.
        }
      }

      await apiService.streamChat(
        {
          message: userMessage.content,
          conversation_id: conversationId || undefined,
          project_id: currentProject?.id,
          context: {
            ...extraContext,
            framework: sessionFramework,
            tms_test_case_id: sessionTmsTestCaseId || undefined,
            platform: sessionPlatform,
            device_name: sessionDeviceName || undefined,
            android_version: sessionAndroidVersion,
            app_url: sessionAppUrl || undefined,
            // Test credentials & OTP
            test_phone: sessionTestPhone || undefined,
            test_email: sessionTestEmail || undefined,
            test_password: sessionTestPassword || undefined,
            otp_provider: sessionOtpProvider !== 'manual' ? sessionOtpProvider : undefined,
            otp_android_device: sessionOtpAndroidDevice || undefined,
            otp_fixed_code: sessionOtpFixedCode || undefined,
            // Active test plan draft for refinement
            ...(activeTestPlanDraft ? { active_test_plan_draft: activeTestPlanDraft } : {}),
            // Active API testing session results for LLM context
            ...(activeTestingSession && testingSession.apiStepResults.length > 0 ? {
              active_api_test_results: {
                session_id: activeTestingSession.session_id,
                url: activeTestingSession.url,
                phase: testingSession.phase,
                total: testingSession.apiStepResults.length,
                passed: testingSession.apiStepResults.filter(r => {
                  const code = r.response?.status_code;
                  return code != null && code >= 200 && code < 300;
                }).length,
                failed: testingSession.apiStepResults.filter(r => {
                  const code = r.response?.status_code;
                  return r.status === 'failed' || (code != null && code >= 400);
                }).length,
                failed_steps: testingSession.apiStepResults
                  .filter(r => {
                    const code = r.response?.status_code;
                    return r.status === 'failed' || (code != null && code >= 400);
                  })
                  .slice(0, 30)
                  .map(r => ({
                    step: r.step_index + 1,
                    method: r.request?.method || r.action_id,
                    path: r.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '',
                    status_code: r.response?.status_code,
                    error: r.error?.slice(0, 200),
                    description: r.description,
                    group: r.group,
                    category: r.category,
                  })),
                passed_steps: testingSession.apiStepResults
                  .filter(r => {
                    const code = r.response?.status_code;
                    return code != null && code >= 200 && code < 300;
                  })
                  .slice(0, 20)
                  .map(r => ({
                    step: r.step_index + 1,
                    method: r.request?.method || r.action_id,
                    path: r.request?.url?.replace(/^https?:\/\/[^/]+/, '') || '',
                    status_code: r.response?.status_code,
                    description: r.description,
                    group: r.group,
                  })),
              },
            } : {}),
          },
        },
        (event: StreamEvent) => {
          // ── CRITICAL: Set provision_browser data BEFORE setMessages ──
          // This runs synchronously and immediately, before any React
          // state updater that could throw or batch unpredictably.
          if (event.type === 'slave_tool_end') {
            const earlyTool = event.tool || '';
            if (earlyTool.includes('provision_browser') && event.output && !event.is_error) {
              try {
                const pbResult = typeof event.output === 'string'
                  ? JSON.parse(event.output) : event.output;
                const lsData = {
                  sessionId: pbResult.session_id || '',
                  liveStreamUrl: pbResult.live_stream_url || '',
                  vncUrl: pbResult.vnc_url || '',
                  livekitToken: pbResult.livekit_token || undefined,
                  livekitUrl: pbResult.livekit_url || undefined,
                  livekitRoom: pbResult.livekit_room || undefined,
                };
                // 1. Ref: synchronous, survives any React timing issues
                provisionBrowserRef.current = lsData;
                // 2. State: triggers re-render for right panel visibility
                setSlaveLiveStream(lsData);
                setSlaveFramework('selenium');
                setSlavePhase('executing');
              } catch { /* ignore parse errors */ }
            }
          }

          // ── Message-level updates (wrapped in try/catch) ──
          // If anything inside throws, the outer handlers below still run.
          try {
          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === assistantMessageId);
            if (msgIndex === -1) return prev;

            const msg = { ...updated[msgIndex] };

            switch (event.type) {
              case 'tool_start':
                msg.toolCalls = [...(msg.toolCalls || []), { tool: event.tool || 'unknown', status: 'running' }];
                break;
              case 'tool_end':
                msg.toolCalls = (msg.toolCalls || []).map((t) =>
                  t.tool === event.tool ? { ...t, status: 'success', result: event.tool_result } : t
                );
                // Handle codebase-specific tool results
                if (event.tool_result?.metadata?.action === 'codebase_analyzed') {
                  msg.codebaseSnapshot = event.tool_result.data;
                }
                if (event.tool_result?.metadata?.action === 'generated_tests_executed') {
                  msg.codebaseTestResults = event.tool_result.data;
                }
                break;
              case 'token':
                // Streamed LLM response tokens — accumulate into content
                msg.content += event.content || '';
                break;
              case 'text':
                // Full or partial text response (non-streaming fallback)
                msg.content += event.content || '';
                break;
              case 'sql_data':
                // Structured SQL data for graphical visualization
                if (event.data) {
                  msg.sqlData = event.data;
                }
                break;
              case 'status_alert':
                // Proactive status alerts from Pillar 4 monitoring
                if (event.alerts && event.alerts.length > 0) {
                  msg.statusAlerts = event.alerts;
                }
                break;
              case 'nl_test_steps':
                if (event.nl_test_plan) {
                  msg.nlTestPlan = event.nl_test_plan;
                }
                break;
              case 'nl_execution_progress':
                if (event.nl_execution_progress) {
                  msg.nlExecutionProgress = event.nl_execution_progress;
                }
                break;
              case 'nl_healing':
                // Merge healing info into execution progress
                if (event.nl_healing && msg.nlExecutionProgress) {
                  msg.nlExecutionProgress = {
                    ...msg.nlExecutionProgress,
                    healed_selectors: [
                      ...(msg.nlExecutionProgress.healed_selectors || []),
                      event.nl_healing,
                    ],
                  };
                }
                break;
              case 'nl_visual_result':
                if (event.nl_visual_result) {
                  msg.nlVisualResult = event.nl_visual_result;
                }
                break;
              case 'nl_exploration':
                if (event.nl_exploration) {
                  msg.nlExplorationResult = event.nl_exploration;
                }
                break;
              case 'site_analysis_progress':
                if (event.site_analysis) {
                  msg.siteAnalysis = event.site_analysis as SiteAnalysisProgress;
                }
                break;
              case 'tms_entity_created':
                // Merge entity into site analysis progress
                if (event.tms_entity && msg.siteAnalysis) {
                  const entity = event.tms_entity as TMSEntityRef;
                  const sa = { ...msg.siteAnalysis };
                  // Add to frontend explorer entities by default
                  if (sa.agents?.frontend_explorer) {
                    sa.agents = {
                      ...sa.agents,
                      frontend_explorer: {
                        ...sa.agents.frontend_explorer,
                        entities_created: [
                          ...(sa.agents.frontend_explorer.entities_created || []),
                          entity,
                        ],
                      },
                    };
                  }
                  msg.siteAnalysis = sa;
                }
                break;
              case 'code_generated':
                if (event.generated_code) {
                  // Route to right panel (APIExecutionPanel → Code Repository tab)
                  // instead of inline GeneratedCodeViewer in chat
                  setSlaveGeneratedCode(event.generated_code as GeneratedCodePayload);
                }
                break;
              case 'live_session_url':
                // Update the site analysis with live stream URL
                if (event.live_session && msg.siteAnalysis) {
                  const sa = { ...msg.siteAnalysis };
                  if (sa.agents?.frontend_explorer) {
                    sa.agents = {
                      ...sa.agents,
                      frontend_explorer: {
                        ...sa.agents.frontend_explorer,
                        live_stream_url: event.live_session.live_stream_url,
                      },
                    };
                  }
                  msg.siteAnalysis = sa;
                }
                break;
              case 'testing_session_created':
                // Interactive testing session handoff
                if (event.testing_session) {
                  msg.testingSession = event.testing_session;
                  console.log('[QA Agent] testing_session_created event received:', event.testing_session);
                }
                break;
              case 'tms_context_prompt':
                // TMS integration context gathering (pre-session)
                if (event.tms_context_prompt) {
                  msg.tmsContextPrompt = event.tms_context_prompt;
                }
                break;
              case 'slave_preflight':
                // Slave pre-flight confirmation card
                if (event.slave_preflight) {
                  msg.slavePreflight = event.slave_preflight;
                  if (event.slave_preflight.discovered_endpoints?.length) {
                    setSlaveDiscoveredEndpoints(event.slave_preflight.discovered_endpoints);
                  }
                }
                break;
              case 'test_plan_draft':
                // Test plan draft — open right panel editor
                if (event.test_plan_draft) {
                  setActiveTestPlanDraft(event.test_plan_draft);
                  msg.testPlanDraft = event.test_plan_draft;
                }
                break;
              case 'coverage_dashboard':
                // Coverage metrics — render visual dashboard card
                if (event.coverage_data) {
                  msg.coverageDashboard = { tool: event.tool || '', data: event.coverage_data };
                }
                break;
              case 'project_context_set':
                // Implicit project creation — auto-switch to the new project
                if (event.project_context) {
                  const { project_id: newProjId, project_name: newProjName } = event.project_context;
                  console.log('[QA Agent] project_context_set:', newProjId, newProjName);
                  // Auto-switch project context with a minimal Project object
                  setCurrentProject({
                    id: String(newProjId),
                    name: newProjName,
                    key: newProjName.replace(/\s+/g, '-').toUpperCase().slice(0, 6),
                    description: null,
                    default_environment: null,
                    default_browser: null,
                    settings: {},
                    is_active: true,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    integrations_count: 0,
                    members_count: 0,
                  });
                  // Refresh project list to include the new project
                  loadAvailableProjects();
                }
                break;
              case 'slave_tool_start': {
                // Slave agent started a tool (Read, Edit, Bash, etc.)
                if (!msg.slaveToolEvents) msg.slaveToolEvents = [];
                msg.slaveToolEvents.push({
                  type: 'slave_tool_start',
                  tool: event.tool || 'unknown',
                  tool_use_id: event.tool_use_id,
                  input: event.input,
                });
                // Intercept HITL tools (request_user_input, security_create_issue)
                const toolStart = event.tool || '';
                if (isHitlTool(toolStart)) {
                  openAgentHitlGate(event, currentSessionId || '');
                } else if (isLoadCapTool(toolStart)) {
                  watchLoadLarge(event, currentSessionId || '');
                }
                // Track API execution in right panel
                if (toolStart.includes('execute_api_request')) {
                  let reqMethod = 'GET';
                  let reqUrl = '';
                  try {
                    const inputJson = JSON.parse(event.input || '{}');
                    reqMethod = (inputJson.method || 'GET').toUpperCase();
                    reqUrl = inputJson.url || inputJson.endpoint || '';
                  } catch { /* ignore parse errors */ }
                  setSlaveApiResults((prev) => [
                    ...prev,
                    {
                      step_index: prev.length,
                      action_id: event.tool_use_id || `api-${prev.length}`,
                      status: 'running',
                      source: 'api_probe',
                      description: `${reqMethod} ${reqUrl}`,
                      request: {
                        method: reqMethod,
                        url: reqUrl,
                        headers: {},
                      },
                    },
                  ]);
                  setSlavePhase('executing');
                }
                // execute_pytest starting → show executing phase
                if (toolStart.includes('execute_pytest')) {
                  setSlavePhase('executing');
                }
                break;
              }
              case 'slave_tool_end': {
                // Slave agent finished a tool
                if (!msg.slaveToolEvents) msg.slaveToolEvents = [];
                const toolEnd = event.tool || '';
                msg.slaveToolEvents.push({
                  type: 'slave_tool_end',
                  tool: event.tool || 'unknown',
                  tool_use_id: event.tool_use_id,
                  output: event.output,
                  is_error: event.is_error,
                  api_result: event.api_result,
                });
                // Clear HITL gate when a HITL tool completes
                if (isHitlTool(toolEnd)) {
                  setAgentHitlGate(null);
                } else if (isLoadCapTool(toolEnd)) {
                  loadWatchRef.current.delete(event.tool_use_id || '');
                  setAgentHitlGate((g) =>
                    g?.eventType === 'load_large' && g.toolUseId === (event.tool_use_id || '') ? null : g,
                  );
                }
                // Update right panel based on tool type

                // Pipeline tool: save_test_artifacts → populate GeneratedCodeViewer
                if (toolEnd.includes('save_test_artifacts') && event.output) {
                  try {
                    const artifactResult = JSON.parse(event.output);
                    if (artifactResult.files) {
                      setSlaveGeneratedCode({
                        files: artifactResult.files.map((f: any) => ({
                          path: f.path,
                          content: f.content,
                          language: 'python',
                          framework: artifactResult.framework || 'pytest',
                        })),
                        summary: {
                          total_files: artifactResult.files.length,
                          total_test_cases: artifactResult.test_count || 0,
                          frameworks_used: [artifactResult.framework || 'pytest'],
                        },
                      });
                    }
                  } catch { /* ignore parse errors */ }
                }

                // Pipeline tool: provision_browser → activate live stream panel
                if (toolEnd.includes('provision_browser') && event.output && !event.is_error) {
                  try {
                    // event.output may be a JSON string or already-parsed object
                    const provisionResult = typeof event.output === 'string'
                      ? JSON.parse(event.output)
                      : event.output;
                    if (provisionResult.live_stream_url || provisionResult.session_id || provisionResult.livekit_token) {
                      setSlaveLiveStream({
                        sessionId: provisionResult.session_id || '',
                        liveStreamUrl: provisionResult.live_stream_url || '',
                        vncUrl: provisionResult.vnc_url || '',
                        livekitToken: provisionResult.livekit_token || undefined,
                        livekitUrl: provisionResult.livekit_url || undefined,
                        livekitRoom: provisionResult.livekit_room || undefined,
                      });
                      setSlaveFramework('selenium');
                      setSlavePhase('executing');
                    }
                  } catch {
                    // ignore parse errors
                  }
                }

                // Pipeline tool: execute_pytest → populate APIExecutionPanel
                // Prefer http_interactions (captured HTTP calls with full req/res details)
                // over per-test results for the Chrome DevTools-style panel.
                // ALWAYS replace existing results — Phase 1 discovery calls (health,
                // openapi.json, login) are just exploration, not test execution.
                if (toolEnd.includes('execute_pytest') && event.output) {
                  try {
                    const execResult = JSON.parse(event.output);
                    const fw: string = execResult.framework || '';
                    const isUIFw = fw === 'playwright' || fw === 'selenium';
                    if (fw) setSlaveFramework(fw);

                    if (execResult.results) {
                      const testResults: APIStepResult[] = execResult.results.map((r: any, i: number) => {
                        const nodeid: string = r.test_name || r.name || r.nodeid || `Test ${i + 1}`;
                        const funcName = nodeid.includes('::') ? nodeid.split('::').pop() || nodeid : nodeid;
                        let category: 'functional' | 'negative' | 'security' = 'functional';
                        const lowerNodeid = nodeid.toLowerCase();
                        if (lowerNodeid.includes('security') || lowerNodeid.includes('injection') || lowerNodeid.includes('xss')) {
                          category = 'security';
                        } else if (lowerNodeid.includes('not_found') || lowerNodeid.includes('invalid') || lowerNodeid.includes('unauthorized') || lowerNodeid.includes('forbidden') || lowerNodeid.includes('error') || lowerNodeid.includes('negative')) {
                          category = 'negative';
                        }

                        // For UI tests (playwright/selenium), skip HTTP method inference
                        if (isUIFw) {
                          return {
                            step_index: i,
                            action_id: `test-${i}`,
                            status: r.status === 'passed' ? 'passed' : 'failed',
                            source: 'test_execution' as const,
                            description: nodeid,
                            duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
                            error: r.error_message || r.longrepr || r.error,
                            category,
                          };
                        }

                        const httpCalls: any[] = r.http_calls || [];
                        const representativeCall = r.status !== 'passed' && httpCalls.length > 0
                          ? httpCalls[httpCalls.length - 1]
                          : httpCalls[0];

                        const inferMethod = (name: string): string => {
                          const lower = name.toLowerCase();
                          if (lower.includes('delete')) return 'DELETE';
                          if (lower.includes('create') || lower.includes('post') || lower.includes('register') || lower.includes('signup') || lower.includes('login')) return 'POST';
                          if (lower.includes('update') || lower.includes('patch')) return 'PATCH';
                          if (lower.includes('put')) return 'PUT';
                          return 'GET';
                        };

                        return {
                          step_index: i,
                          action_id: `test-${i}`,
                          status: r.status === 'passed' ? 'passed' : 'failed',
                          source: 'test_execution' as const,
                          description: nodeid,
                          duration_ms: r.duration_ms || (r.duration ? r.duration * 1000 : undefined),
                          error: r.error_message || r.longrepr || r.error,
                          category,
                          total_steps: httpCalls.length || undefined,
                          http_calls: httpCalls.length > 0 ? httpCalls.map((hc: any) => ({
                            method: hc.method || 'GET',
                            url: hc.url || '',
                            status_code: hc.status_code || 0,
                            duration_ms: hc.duration_ms,
                          })) : undefined,
                          request: representativeCall ? {
                            method: representativeCall.method,
                            url: representativeCall.url,
                            headers: representativeCall.request?.headers || {},
                            body: representativeCall.request?.body || undefined,
                          } : {
                            method: inferMethod(funcName),
                            url: funcName,
                            headers: {},
                          },
                          response: representativeCall?.status_code ? {
                            status_code: representativeCall.status_code,
                            headers: representativeCall.response?.headers || {},
                            body: representativeCall.response?.body || '',
                            content_type: representativeCall.response?.headers?.['content-type'] || '',
                            duration_ms: representativeCall.duration_ms || 0,
                          } : undefined,
                        };
                      });
                      // Accumulate across batches. This previously kept only
                      // api_probe rows and dropped every earlier test_execution
                      // result, so a suite run in N batches displayed only the
                      // last batch — ~50 tests executed, "2/2" shown.
                      //
                      // De-duplicated by test nodeid (`description`) so a retried
                      // test updates in place instead of appearing twice, and
                      // step_index/action_id are re-derived across the merged set
                      // because they are per-batch indices that would otherwise
                      // collide between batches.
                      setSlaveApiResults((prev) => {
                        const merged = [...prev];
                        for (const incoming of testResults) {
                          const key = incoming.description;
                          const at = key
                            ? merged.findIndex(
                                (r) => r.source === 'test_execution' && r.description === key
                              )
                            : -1;
                          if (at >= 0) {
                            merged[at] = incoming;   // retry supersedes
                          } else {
                            merged.push(incoming);
                          }
                        }
                        return merged.map((r, idx) =>
                          r.source === 'test_execution'
                            ? { ...r, step_index: idx, action_id: `test-${idx}` }
                            : r
                        );
                      });
                    }
                    if (execResult.tms_integration) {
                      const runInfo = execResult.tms_integration;
                      setSlaveTmsIntegration(prev => {
                        const runStats = runInfo.stats || {};
                        const newRun = {
                          test_run_id: runInfo.test_run_id || runInfo.id,
                          test_run_url: runInfo.test_run_url || runInfo.url || '',
                          name: runInfo.name || '',
                          stats: {
                            total: runStats.total_tests || runStats.total || execResult.total || 0,
                            passed: runStats.passed || execResult.passed || 0,
                            failed: runStats.failed || execResult.failed || 0,
                          },
                        };
                        // Accumulate: seed from previous runs or legacy single-run
                        const existingRuns = prev?.runs
                          || (prev?.test_run_id ? [{ test_run_id: prev.test_run_id, test_run_url: prev.test_run_url || '', name: '', stats: { total: prev.stats?.total || 0, passed: prev.stats?.passed || 0, failed: prev.stats?.failed || 0 } }] : []);
                        const runs = [...existingRuns, newRun];
                        const total_stats = runs.reduce((a, r) => ({ total: a.total + r.stats.total, passed: a.passed + r.stats.passed, failed: a.failed + r.stats.failed }), { total: 0, passed: 0, failed: 0 });
                        return {
                          ...runInfo,
                          runs,
                          total_stats,
                          stats: total_stats,
                          reporting: { mode: 'post-execution', results_reported: total_stats.total },
                        };
                      });
                    }
                    setSlavePhase(execResult.success ? 'completed' : 'failed');
                  } catch { /* ignore parse errors */ }
                }

                // TMS tool: tms_create_test_run → append new run to TMS tab
                if (toolEnd.includes('tms_create_test_run') && event.output) {
                  try {
                    const runResult = JSON.parse(event.output);
                    if (runResult.id || runResult.test_run_id) {
                      setSlaveTmsIntegration(prev => {
                        const newRun = {
                          test_run_id: runResult.id || runResult.test_run_id,
                          test_run_url: runResult.url || '',
                          name: runResult.name || '',
                          stats: { total: 0, passed: 0, failed: 0 },
                        };
                        // Collect existing runs, seeding from legacy single-run if needed
                        const existingRuns = prev?.runs
                          || (prev?.test_run_id ? [{ test_run_id: prev.test_run_id, test_run_url: prev.test_run_url || '', name: '', stats: { total: prev.stats?.total || 0, passed: prev.stats?.passed || 0, failed: prev.stats?.failed || 0 } }] : []);
                        const runs = [...existingRuns, newRun];
                        const total_stats = runs.reduce((a, r) => ({ total: a.total + r.stats.total, passed: a.passed + r.stats.passed, failed: a.failed + r.stats.failed }), { total: 0, passed: 0, failed: 0 });
                        return {
                          ...prev,
                          test_run_id: newRun.test_run_id,
                          test_run_url: newRun.test_run_url,
                          organization_id: runResult.organization_id || prev?.organization_id || 0,
                          project_id: runResult.project_id || prev?.project_id || 0,
                          framework: runResult.framework || prev?.framework || 'pytest',
                          plugin_mode: false,
                          runs,
                          total_stats,
                          stats: total_stats,
                          reporting: {
                            mode: 'post-execution',
                            results_reported: total_stats.total,
                            ...(prev?.reporting || {}),
                          },
                        } as any;
                      });
                    }
                  } catch { /* ignore parse errors */ }
                }

                // TMS tool: tms_report_result → update last run's stats in TMS tab
                if (toolEnd.includes('tms_report_result') && event.output) {
                  try {
                    const reportResult = JSON.parse(event.output);
                    if (reportResult && !reportResult.error) {
                      setSlaveTmsIntegration(prev => {
                        if (!prev) return prev;
                        const runs = [...(prev.runs || [])];
                        if (runs.length > 0) {
                          const lastRun = { ...runs[runs.length - 1], stats: { ...runs[runs.length - 1].stats } };
                          lastRun.stats.total += 1;
                          const status = reportResult.status;
                          if (status === 'PASSED') lastRun.stats.passed += 1;
                          else if (status === 'FAILED') lastRun.stats.failed += 1;
                          runs[runs.length - 1] = lastRun;
                        }
                        const total_stats = runs.reduce((a, r) => ({ total: a.total + r.stats.total, passed: a.passed + r.stats.passed, failed: a.failed + r.stats.failed }), { total: 0, passed: 0, failed: 0 });
                        return {
                          ...prev,
                          runs,
                          total_stats,
                          stats: total_stats,
                          reporting: {
                            ...(prev.reporting || { mode: 'post-execution' }),
                            results_reported: total_stats.total,
                          },
                        };
                      });
                    }
                  } catch { /* ignore parse errors */ }
                }

                // Update API execution result in right panel
                if (toolEnd.includes('execute_api_request') && event.api_result) {
                  const ar = event.api_result;
                  // Normalize status: "error" → "failed" for verdict logic
                  const normalizedStatus = ar.status === 'error' || ar.status === 'timeout'
                    ? 'failed'
                    : ar.status || (event.is_error ? 'failed' : 'passed');
                  // Only build response object if we have a real status code
                  const resp = ar.response;
                  const hasResponse = resp && resp.status_code;
                  const errorMsg: string | undefined = ar.error
                    || (normalizedStatus === 'failed' && !ar.status_code
                      ? 'Request timed out' : undefined);
                  setSlaveApiResults((prev) =>
                    prev.map((r): APIStepResult =>
                      r.action_id === event.tool_use_id
                        ? {
                            ...r,
                            status: normalizedStatus,
                            duration_ms: ar.duration_ms,
                            error: errorMsg,
                            request: ar.request || r.request,
                            response: hasResponse
                              ? {
                                  status_code: Number(resp.status_code) || 0,
                                  headers: (resp.headers as Record<string, string>) || {},
                                  body: resp.body,
                                  content_type: resp.content_type || '',
                                  duration_ms: resp.duration_ms || ar.duration_ms || 0,
                                }
                              : undefined,
                          }
                        : r
                    )
                  );
                } else if (toolEnd.includes('execute_api_request') && event.is_error) {
                  // No api_result but tool errored — mark as failed
                  setSlaveApiResults((prev) =>
                    prev.map((r) =>
                      r.action_id === event.tool_use_id
                        ? { ...r, status: 'failed', error: event.output }
                        : r
                    )
                  );
                } else if (toolEnd.includes('execute_api_request')) {
                  // No api_result and no error — try parsing output for status
                  let parsedStatus = 'failed';
                  let parsedCode: number | undefined;
                  let parsedError: string | undefined;
                  let parsedDuration: number | undefined;
                  try {
                    const parsed = JSON.parse(event.output || '{}');
                    const rawStatus = parsed.status || 'failed';
                    // Normalize: error/timeout → failed
                    parsedStatus = (rawStatus === 'error' || rawStatus === 'timeout')
                      ? 'failed' : rawStatus;
                    parsedCode = parsed.status_code;
                    parsedError = parsed.error;
                    parsedDuration = parsed.duration_ms;
                  } catch { /* ignore */ }
                  setSlaveApiResults((prev) =>
                    prev.map((r) =>
                      r.action_id === event.tool_use_id
                        ? {
                            ...r,
                            status: parsedStatus,
                            error: parsedError,
                            duration_ms: parsedDuration || r.duration_ms,
                            ...(parsedCode != null
                              ? {
                                  response: {
                                    status_code: parsedCode,
                                    headers: {},
                                    body: '',
                                    content_type: '',
                                    duration_ms: 0,
                                  },
                                }
                              : {}),
                          }
                        : r
                    )
                  );
                }

                // Detect test plan data from slave tool results
                if ((toolEnd === 'list_test_plans' || toolEnd === 'get_test_plan' || toolEnd === 'update_test_plan') && event.output && !event.is_error) {
                  try {
                    const parsed = JSON.parse(event.output);
                    if (parsed.status === 'success') {
                      if (toolEnd === 'list_test_plans' && parsed.data?.test_plans) {
                        const items = parsed.data.test_plans;
                        if (items.length > 0) {
                          setTestPlanDetailItems(items);
                          setTestPlanDetailSelected(items[0]);
                        }
                      } else if (toolEnd === 'get_test_plan' && parsed.data) {
                        const plan = parsed.data;
                        setTestPlanDetailItems([plan]);
                        setTestPlanDetailSelected(plan);
                      } else if (toolEnd === 'update_test_plan' && parsed.data) {
                        const updatedPlan = parsed.data;
                        setTestPlanDetailItems((prev) => {
                          if (!prev) return prev;
                          return prev.map((item) =>
                            item.id === updatedPlan.id ? { ...item, ...updatedPlan } : item
                          );
                        });
                        setTestPlanDetailSelected((prev: any) =>
                          prev?.id === updatedPlan.id ? { ...prev, ...updatedPlan } : prev
                        );
                      }
                    }
                  } catch { /* output may be truncated — ignore parse errors */ }
                }
                break;
              }
              case 'slave_complete':
                // Slave agent session completed
                msg.slaveComplete = applySlaveComplete(event);
                // Always transition to completed — the slave session
                // finishing is the definitive signal.  Don't gate on
                // React state (slaveApiResults/slaveGeneratedCode) because
                // those values are stale when events arrive as a burst
                // due to React's state batching.
                setSlavePhase('completed');
                break;
              case 'progress':
              case 'keepalive':
              case 'status':
                // Progress/keepalive/status events — no content change, keep connection alive
                break;
              case 'approval_required':
                msg.approval = event.approval;
                break;
              case 'security_scan_started':
                msg.securityScanStatus = 'scanning';
                break;
              case 'security_scan_progress':
                msg.securityScanProgress = event.progress;
                break;
              case 'complete':
                msg.status = 'complete';
                setIsStreaming(false);
                // Refresh session list to show updated title
                setSessionRefreshTrigger((prev) => prev + 1);
                break;
              case 'error':
                msg.status = 'error';
                msg.content = event.message || 'An error occurred';
                setError(event.message || 'An error occurred');
                setIsStreaming(false);
                break;
            }

            updated[msgIndex] = msg;
            return updated;
          });
          } catch (msgErr) {
            // setMessages failed — log but don't block outer handlers
            console.error('[QA Agent] setMessages error:', msgErr);
          }

          // ── Session-level state updates (outside setMessages) ──────
          // These MUST run outside setMessages() because React state
          // updater functions must be pure (no side-effect set* calls).
          // The msgIndex === -1 early return above would also skip these
          // if the assistant message hasn't been added to the array yet.
          // Only idempotent setters are duplicated here — accumulating
          // setters (setSlaveApiResults, setSlaveTmsIntegration) stay
          // inside setMessages only, as duplicating them would cause
          // double-entries.
          if (event.type === 'slave_tool_end') {
            const toolNameOuter = event.tool || '';
            // provision_browser → activate live stream right panel
            if (toolNameOuter.includes('provision_browser') && event.output && !event.is_error) {
              try {
                const result = typeof event.output === 'string'
                  ? JSON.parse(event.output) : event.output;
                if (result.live_stream_url || result.session_id || result.livekit_token) {
                  setSlaveLiveStream({
                    sessionId: result.session_id || '',
                    liveStreamUrl: result.live_stream_url || '',
                    vncUrl: result.vnc_url || '',
                    livekitToken: result.livekit_token || undefined,
                    livekitUrl: result.livekit_url || undefined,
                    livekitRoom: result.livekit_room || undefined,
                  });
                  setSlaveFramework('selenium');
                  setSlavePhase('executing');
                }
              } catch { /* ignore parse errors */ }
            }
            // HITL tool completed → clear HITL gate
            if (isHitlTool(toolNameOuter)) {
              setAgentHitlGate(null);
            } else if (isLoadCapTool(toolNameOuter)) {
              loadWatchRef.current.delete(event.tool_use_id || '');
              setAgentHitlGate((g) =>
                    g?.eventType === 'load_large' && g.toolUseId === (event.tool_use_id || '') ? null : g,
                  );
            }
            // execute_pytest completed → update phase
            if (toolNameOuter.includes('execute_pytest') && event.output) {
              try {
                const execResult = JSON.parse(event.output);
                const fw: string = execResult.framework || '';
                if (fw) setSlaveFramework(fw);
                setSlavePhase(execResult.success ? 'completed' : 'failed');
              } catch { /* ignore */ }
            }
            // save_test_artifacts → generated code
            if (toolNameOuter.includes('save_test_artifacts') && event.output) {
              try {
                const artifactResult = JSON.parse(event.output);
                if (artifactResult.files) {
                  setSlaveGeneratedCode({
                    files: artifactResult.files.map((f: any) => ({
                      path: f.path,
                      content: f.content,
                      language: 'python',
                      framework: artifactResult.framework || 'pytest',
                    })),
                    summary: {
                      total_files: artifactResult.files.length,
                      total_test_cases: artifactResult.test_count || 0,
                      frameworks_used: [artifactResult.framework || 'pytest'],
                    },
                  });
                }
              } catch { /* ignore */ }
            }
          } else if (event.type === 'slave_tool_start') {
            const toolNameOuter = event.tool || '';
            // HITL tool (request_user_input, security_create_issue) → show HITL gate
            if (isHitlTool(toolNameOuter)) {
              openAgentHitlGate(event, currentSessionId || '');
            } else if (isLoadCapTool(toolNameOuter)) {
              watchLoadLarge(event, currentSessionId || '');
            }
            if (toolNameOuter.includes('execute_pytest') || toolNameOuter.includes('execute_api_request')) {
              setSlavePhase('executing');
            }
          } else if (event.type === 'slave_complete') {
            setSlavePhase('completed');
          } else if (event.type === 'code_generated') {
            if (event.generated_code) {
              setSlaveGeneratedCode(event.generated_code as GeneratedCodePayload);
            }
          } else if (event.type === 'slave_preflight') {
            if (event.slave_preflight?.discovered_endpoints?.length) {
              setSlaveDiscoveredEndpoints(event.slave_preflight.discovered_endpoints);
            }
          } else if (event.type === 'test_plan_draft') {
            if (event.test_plan_draft) {
              setActiveTestPlanDraft(event.test_plan_draft);
            }
          } else if (event.type === 'project_context_set') {
            if (event.project_context) {
              const { project_id: newProjId, project_name: newProjName } = event.project_context;
              setCurrentProject({
                id: String(newProjId),
                name: newProjName,
                key: newProjName.replace(/\s+/g, '-').toUpperCase().slice(0, 6),
                description: null,
                default_environment: null,
                default_browser: null,
                settings: {},
                is_active: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                integrations_count: 0,
                members_count: 0,
              });
              loadAvailableProjects();
            }
          } else if (event.type === 'complete') {
            setIsStreaming(false);
            setSessionRefreshTrigger((prev) => prev + 1);
            // Thumbs need the saved row's id, which the backend puts in the complete metadata.
            const savedId = event.metadata?.message_id;
            if (typeof savedId === 'string' && savedId) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMessageId ? { ...m, savedMessageId: savedId } : m))
              );
            }
          } else if (event.type === 'error') {
            setError(event.message || 'An error occurred');
            setIsStreaming(false);
          }

          if (event.type === 'warning' || event.type === 'error') {
            const notice = walletNoticeFromEvent(event);
            if (notice) setWalletNotice(notice);
          }
        },
        abortController.signal
      );
    } catch (e: any) {
      // Ignore abort errors from session switching
      if (e.name === 'AbortError') return;
      const rawMsg = e.message || 'Failed to send message';
      console.error('Chat stream error:', rawMsg);
      // Translate low-level network errors into user-friendly messages
      const isConnectionLost = /connection.*lost|lost.*connection/i.test(rawMsg);
      const isNetworkError = /network|fetch|ERR_CONNECTION|ECONNREFUSED/i.test(rawMsg);
      const errMsg = isConnectionLost
        ? 'Connection to the server was lost during processing. The backend may still be running your request — check results before retrying.'
        : isNetworkError
        ? 'Unable to reach the server. The backend may be restarting or temporarily unavailable. Please try again in a few seconds.'
        : rawMsg;
      setError(errMsg);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId ? { ...m, status: 'error', content: errMsg } : m
        )
      );
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  }, [input, isLoading, currentSessionId, currentProject, sessionFramework, sessionTmsTestCaseId, sessionPlatform, sessionDeviceName, sessionAndroidVersion, sessionAppUrl, sessionTestPhone, sessionTestEmail, sessionTestPassword, sessionOtpProvider, sessionOtpAndroidDevice, sessionOtpFixedCode, activeTestingSession, activeTestPlanDraft, tryRouteCommand]);

  // Receive recorded steps from the full-screen /record tab and ask the agent
  // to generate a test from them. Also picks up a stash left in localStorage if
  // the record page was opened without an opener.
  useEffect(() => {
    const genFromSteps = (steps: unknown) => {
      if (!Array.isArray(steps) || steps.length === 0) return;
      sendMessage(
        'This is a recorded UI journey that usually exercises SEVERAL distinct features. '
        + 'Do NOT generate one monolithic test. Instead:\n'
        + '1. Segment the flow into focused, independently-meaningful test cases — one per '
        + 'behaviour (e.g. login, create project, invite member, browse settings tabs, '
        + 'dashboard widgets, create module, create test suite, create test case, create '
        + 'test run, update result status). Each = one test function with a clear name + docstring.\n'
        + '2. Make each test independent and re-runnable: use pytest fixtures to establish its '
        + 'preconditions (e.g. a project/module/suite fixture) rather than depending on a prior '
        + "test having run. Where a real precondition can't be isolated, note it.\n"
        + '3. Register a corresponding TMS test case for EACH (not one).\n'
        + '4. Use the Test Brief for credentials. Flag any real side-effects (e.g. emails sent) '
        + 'and non-deterministic actions (skip them). Prefer text/role locators over recorded '
        + 'nth-of-type chains.\n'
        + 'Then save to the workspace/repo.\n'
        + 'Recorded steps (JSON):\n```json\n' + JSON.stringify(steps, null, 2) + '\n```',
      );
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'qa-record-steps') genFromSteps(e.data.steps);
    };
    window.addEventListener('message', onMessage);
    try {
      const stashed = localStorage.getItem('qa_record_steps');
      if (stashed) { localStorage.removeItem('qa_record_steps'); genFromSteps(JSON.parse(stashed)); }
    } catch { /* ignore */ }
    return () => window.removeEventListener('message', onMessage);
  }, [sendMessage]);

  const handleTMSContextSelect = useCallback((
    prompt: TMSContextPrompt,
    choice: string,
    planId?: number,
    scope?: string,
    selectedTestCaseIds?: number[],
  ) => {
    // Re-send the original message with TMS context baked into extra context
    sendMessage(prompt.original_request, {
      tms_integration_choice: choice,
      ...(planId != null ? { tms_plan_id: planId } : {}),
      testing_scope: scope || 'full_crawl',
      ...(selectedTestCaseIds && selectedTestCaseIds.length > 0
        ? { selected_test_case_ids: selectedTestCaseIds }
        : {}),
    });
  }, [sendMessage]);

  const handleSlavePreflightConfirm = useCallback(async (
    originalRequest: string,
    options?: SlavePreflightConfirmOptions,
  ) => {
    const approvalId = options?.approval_id;
    if (!approvalId) {
      // Card from before the server-side gate: it cannot be approved.
      const err: Error & { status?: number } = new Error('This approval expired, ask again.');
      err.status = 410;
      throw err;
    }
    // Record the decision server-side first; only a 2xx lets the run start.
    // A 410 (expired) rejects here and the card shows it; nothing is re-sent.
    await apiService.handleApproval(approvalId, 'approve');
    sendMessage(originalRequest, {
      slave_approval_id: approvalId,
      ...(options?.selected_endpoints ? { selected_endpoints: options.selected_endpoints } : {}),
      ...(options?.auth_config ? { auth_config: options.auth_config } : {}),
      ...(options?.total_discovered_count != null ? { total_discovered_count: options.total_discovered_count } : {}),
    });
  }, [sendMessage]);

  const handleSaveTestPlan = async () => {
    if (!activeTestPlanDraft) return;
    setIsTestPlanSaving(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';
      const resp = await fetch(`${API_URL}/chat/test-plan/save`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: currentSessionId,
          project_id: currentProject?.id,
          test_plan: activeTestPlanDraft,
        }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Save failed');
      }
      const result = await resp.json();
      const savedName = activeTestPlanDraft.name;
      setActiveTestPlanDraft(null);
      setMessages((prev) => [
        ...prev,
        {
          id: `tp-save-${Date.now()}`,
          role: 'assistant',
          content: `Test plan "${savedName}" saved to TMS successfully${result.data?.id ? ` (ID: ${result.data.id})` : ''}.`,
          timestamp: new Date(),
          status: 'complete',
        },
      ]);
    } catch (e: any) {
      setError(`Failed to save test plan: ${e.message}`);
    } finally {
      setIsTestPlanSaving(false);
    }
  };

  const handleDiscardTestPlan = () => {
    setActiveTestPlanDraft(null);
    setMessages((prev) => [
      ...prev,
      {
        id: `tp-discard-${Date.now()}`,
        role: 'assistant',
        content: 'Test plan draft discarded.',
        timestamp: new Date(),
        status: 'complete',
      },
    ]);
  };

  const handleAgentHitlResponse = async (response: Record<string, any>) => {
    if (!agentHitlGate) return;
    try {
      await submitHitlResponse(
        agentHitlGate.sessionId,
        agentHitlGate.requestId,
        response,
      );
      setAgentHitlGate(null);
    } catch (err: any) {
      console.error('HITL response failed:', err);
    }
  };

  const handleApprove = async (approvalId: string) => {
    // Show a loading message while executing
    const loadingMsgId = `approval-loading-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: loadingMsgId,
        role: 'assistant',
        content: 'Executing approved action...',
        timestamp: new Date(),
        status: 'streaming',
      },
    ]);

    try {
      const result = await apiService.handleApproval(approvalId, 'approve');

      // Build the result message
      let resultContent = '';
      let msgStatus: 'complete' | 'streaming' = 'complete';
      if (result.execution_result) {
        resultContent = result.execution_result.message || 'Action executed successfully.';
      } else {
        // Execution is running in the background (async)
        resultContent = 'Action approved. Executing in background — the result will appear here shortly...';
        msgStatus = 'streaming';

        // Poll the conversation for the execution result
        const conversationId = currentSessionId;
        if (conversationId) {
          const pollInterval = setInterval(async () => {
            try {
              const session = await sessionService.getSession(conversationId);
              const messages = session?.messages || [];
              // Look for a message with this approval_id in metadata
              const resultMsg = messages.find(
                (m: any) =>
                  m.metadata?.approval_id === approvalId &&
                  m.role === 'assistant'
              );
              if (resultMsg) {
                clearInterval(pollInterval);
                // If the backend returned a test_plan_draft, open the editor
                if (resultMsg.metadata?.test_plan_draft) {
                  setActiveTestPlanDraft(resultMsg.metadata.test_plan_draft);
                }
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === loadingMsgId
                      ? { ...m, content: resultMsg.content, status: 'complete' }
                      : m
                  )
                );
              }
            } catch {
              // Ignore polling errors
            }
          }, 5000);

          // Stop polling after 10 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === loadingMsgId && m.status === 'streaming'
                  ? { ...m, content: 'Action is still executing. Refresh the chat to see the result.', status: 'complete' }
                  : m
              )
            );
          }, 600000);
        }
      }

      // Replace loading message with result
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsgId
            ? { ...m, content: resultContent, status: msgStatus }
            : m
        )
      );

      // Also remove the approval buttons from the original message
      setMessages((prev) =>
        prev.map((m) =>
          m.approval?.approval_id === approvalId
            ? { ...m, approval: undefined }
            : m
        )
      );
    } catch (e: any) {
      // Replace loading message with error
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsgId
            ? { ...m, content: `Failed to execute action: ${e.message}`, status: 'error' }
            : m
        )
      );
      setError(e.message);
    }
  };

  const handleReject = async (approvalId: string) => {
    try {
      await apiService.handleApproval(approvalId, 'reject');
      setMessages((prev) => [
        ...prev,
        {
          id: `rejection-${Date.now()}`,
          role: 'assistant',
          content: 'Action rejected. Let me know if you need anything else.',
          timestamp: new Date(),
          status: 'complete',
        },
      ]);

      // Remove approval buttons from the original message
      setMessages((prev) =>
        prev.map((m) =>
          m.approval?.approval_id === approvalId
            ? { ...m, approval: undefined }
            : m
        )
      );
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    setInput(prompt);
    setShowToolsDialog(false);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleProjectChange = (project: Project | null) => {
    setCurrentProject(project);
    // Clear conversation when project changes
    if (project) {
      setCurrentSessionId(null);
      setMessages([]);
    }
  };

  const handleInlineProjectSelect = (project: Project) => {
    // Called from the inline project picker in SelectProjectPrompt
    setCurrentProject(project);
    setCurrentSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Show settings page if requested (only when a project is selected)
  if (showSettings && currentProject) {
    return (
      <IntegrationsPage
        project={currentProject}
        onBack={handleCloseSettings}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <AgentIcon color="primary" sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ mr: 3 }}>
            QA AI Agent
          </Typography>

          {/* Project Selector — locked when a conversation has messages */}
          <ProjectSelector
            onProjectChange={handleProjectChange}
            onSettingsClick={currentProject ? handleOpenSettings : undefined}
            locked={messages.length > 0}
          />

          {/* Where this project's tests are delivered as PRs (GitHub App). */}
          <RepositoryPicker projectId={currentProject?.id || null} />

          {/* Always-available domain authorization — the agent may refuse an
              unauthorized target before any pre-flight card renders, so this
              gives an admin a way to authorize it from the header directly. */}
          <AuthorizedDomainsManager
            projectId={currentProject?.id || null}
            canManage={canAuthorizeDomain}
          />

          {/* The project owner's testing brief — authoritative context the
              agent reads before every run (auth, scope, rules, assertions). */}
          <TestBriefPanel projectId={currentProject?.id || null} />

          {/* Postman-style API/Security request collection — the editable plan
              for API & security testing. "Run" hands it to the agent. */}
          <ApiWorkbenchPanel
            projectId={currentProject?.id || null}
            onRunInAgent={() =>
              sendMessage(
                'Do API and security testing using the API Workbench collection for this project. '
                + 'Call get_api_workbench first and treat it as the authoritative plan.',
              )
            }
          />

          {/* Record a flow in a full-screen tab → agent generates the test. */}
          <Tooltip title="Record a flow → generate a test (opens a full-screen tab)">
            <Button
              size="small" variant="outlined" color="error" startIcon={<RecordIcon />}
              onClick={() => window.open(
                `/record?projectId=${currentProject?.id || ''}`, '_blank',
              )}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              Record
            </Button>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {/* Tools Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<TipsIcon />}
            onClick={() => setShowToolsDialog(true)}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            Tools & Tips
          </Button>

          <Chip
            size="small"
            label={agentStatus === 'online' ? 'Online' : agentStatus === 'offline' ? 'Offline' : 'Checking...'}
            color={agentStatus === 'online' ? 'success' : agentStatus === 'offline' ? 'error' : 'default'}
            icon={agentStatus === 'online' ? <SuccessIcon /> : agentStatus === 'offline' ? <ErrorIcon /> : undefined}
            sx={{ mr: 2 }}
          />

          {user && (
            <>
              <Button
                color="inherit"
                onClick={handleMenuOpen}
                startIcon={
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    {user.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                  </Avatar>
                }
              >
                {user.username || user.email}
              </Button>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem disabled>
                  <PersonIcon sx={{ mr: 1 }} />
                  {user.email}
                </MenuItem>
                <Divider />
                <MenuItem component={RouterLink} to="/usage" onClick={handleMenuClose}>
                  <UsageIcon sx={{ mr: 1 }} />
                  Usage
                </MenuItem>
                {canAuthorizeDomain && (
                  <MenuItem component={RouterLink} to="/admin/metrics" onClick={handleMenuClose}>
                    <UsageIcon sx={{ mr: 1 }} />
                    Agent metrics
                  </MenuItem>
                )}
                {currentProject && (
                  <MenuItem onClick={() => { handleMenuClose(); handleOpenSettings(); }}>
                    <SettingsIcon sx={{ mr: 1 }} />
                    Integration Settings
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <WalletBanner notice={walletNotice} />

      {/* Main Content */}
      <Box ref={containerRef} sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Chat History Sidebar — collapsible via toggle button */}
        <Box sx={{
          width: sidebarCollapsed ? 0 : { xs: 0, md: activeTestingSession ? '15%' : '25%' },
          minWidth: sidebarCollapsed ? 0 : { md: activeTestingSession ? 160 : 200 },
          flexShrink: 0,
          height: '100%',
          display: { xs: 'none', md: 'block' },
          transition: 'width 0.3s ease, min-width 0.3s ease',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <ChatHistorySidebar
            currentSessionId={currentSessionId}
            projectId={currentProject?.id || undefined}
            onNewChat={handleNewChat}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
            refreshTrigger={sessionRefreshTrigger}
          />
        </Box>
        {/* Sidebar toggle button */}
        <Box
          onClick={() => setSidebarCollapsed(prev => !prev)}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
          sx={{
            width: 28,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'grey.200',
            borderLeft: 1,
            borderRight: 1,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.200' },
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon sx={{ fontSize: 20, color: 'grey.700' }} />
          ) : (
            <ChevronLeftIcon sx={{ fontSize: 20, color: 'grey.700' }} />
          )}
        </Box>

        {/* Chat Area — fills remaining space, narrows when right panel active */}
        <Box
          sx={{
            flex: hasRightPanel ? 'none' : 1,
            width: hasRightPanel ? `${chatWidthPercent}%` : undefined,
            flexShrink: 0,
            height: '100%',
            transition: isDragging ? 'none' : 'width 0.3s ease',
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
            {/* Error Alert */}
            <Collapse in={!!error}>
              <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
                {error}
              </Alert>
            </Collapse>

            {/* Messages Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: activeTestingSession ? 1.5 : 3, minHeight: 0 }}>
              {messages.length === 0 ? (
                <WelcomeMessage onQuickAction={handleQuickPrompt} />
              ) : (
                <>
                  {messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      activeTestingSessionId={activeTestingSession?.session_id}
                      onTMSContextSelect={handleTMSContextSelect}
                      onSlavePreflightConfirm={handleSlavePreflightConfirm}
                      preflightProjectId={currentProject?.id}
                      canAuthorizeDomain={canAuthorizeDomain}
                      isSelected={selectedMessageId === message.id}
                      onSelectMessage={handleSelectMessage}
                      artifactInfo={getMessageArtifactInfo(message)}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Input Area */}
            <Box sx={{ p: activeTestingSession ? 1 : 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              {/* Session Config Toolbar */}
              {!activeTestingSession && (
                <Box sx={{ maxWidth: 800, mx: 'auto', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={sessionFramework === 'playwright' ? 'Playwright' : 'Selenium'}
                      size="small"
                      variant="outlined"
                      onClick={() => setSessionFramework(f => f === 'playwright' ? 'selenium' : 'playwright')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label={sessionPlatform === 'linux' ? 'Browser' : 'Android'}
                      size="small"
                      variant="outlined"
                      color={sessionPlatform === 'android' ? 'secondary' : 'default'}
                      onClick={() => setSessionPlatform(p => p === 'linux' ? 'android' : 'linux')}
                      sx={{ cursor: 'pointer' }}
                    />
                    {sessionTmsTestCaseId && (
                      <Chip label={`TMS: ${sessionTmsTestCaseId}`} size="small" variant="outlined" color="info" onDelete={() => setSessionTmsTestCaseId('')} />
                    )}
                    <Chip
                      icon={<PersonIcon />}
                      label={sessionTestPhone || sessionTestEmail ? `Login: ${sessionTestPhone || sessionTestEmail}` : 'Login / OTP'}
                      size="small"
                      variant={showLoginConfig ? 'filled' : 'outlined'}
                      color={sessionTestPhone || sessionTestEmail ? 'success' : 'default'}
                      onClick={() => setShowLoginConfig(!showLoginConfig)}
                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                    />
                    <Chip
                      label="Config"
                      size="small"
                      variant={showSessionConfig ? 'filled' : 'outlined'}
                      onClick={() => setShowSessionConfig(!showSessionConfig)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>

                  {/* Login Credentials — separate prominent section */}
                  <Collapse in={showLoginConfig}>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200',
                               background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
                      <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ mb: 1, display: 'block' }}>
                        Login Credentials & OTP — auto-fills login/signup forms during testing
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                          size="small" label="Phone Number" sx={{ width: 170 }}
                          value={sessionTestPhone}
                          onChange={(e) => setSessionTestPhone(e.target.value)}
                          placeholder="+91 98765 43210"
                        />
                        <TextField
                          size="small" label="Email" sx={{ width: 200 }}
                          value={sessionTestEmail}
                          onChange={(e) => setSessionTestEmail(e.target.value)}
                          placeholder="test@example.com"
                        />
                        <TextField
                          size="small" label="Password" type="password" sx={{ width: 150 }}
                          value={sessionTestPassword}
                          onChange={(e) => setSessionTestPassword(e.target.value)}
                        />
                        <TextField
                          size="small" label="OTP Method" select sx={{ width: 150 }}
                          value={sessionOtpProvider}
                          onChange={(e) => setSessionOtpProvider(e.target.value as any)}
                        >
                          <MenuItem value="manual">Manual</MenuItem>
                          <MenuItem value="android_sms">Android SMS</MenuItem>
                          <MenuItem value="fixed">Fixed Code</MenuItem>
                        </TextField>
                        {sessionOtpProvider === 'android_sms' && (
                          <TextField
                            size="small" label="Android Device" sx={{ width: 170 }}
                            value={sessionOtpAndroidDevice}
                            onChange={(e) => setSessionOtpAndroidDevice(e.target.value)}
                            placeholder="e.g. 192.168.29.173"
                          />
                        )}
                        {sessionOtpProvider === 'fixed' && (
                          <TextField
                            size="small" label="OTP Code" sx={{ width: 120 }}
                            value={sessionOtpFixedCode}
                            onChange={(e) => setSessionOtpFixedCode(e.target.value)}
                            placeholder="123456"
                          />
                        )}
                      </Box>
                    </Box>
                  </Collapse>

                  {/* Advanced Config */}
                  <Collapse in={showSessionConfig}>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                          size="small" label="TMS Test Case ID" sx={{ width: 160 }}
                          value={sessionTmsTestCaseId}
                          onChange={(e) => setSessionTmsTestCaseId(e.target.value)}
                          placeholder="e.g. TC-3019"
                        />
                        {sessionPlatform === 'android' && (
                          <>
                            <TextField
                              size="small" label="Device" sx={{ width: 160 }}
                              value={sessionDeviceName}
                              onChange={(e) => setSessionDeviceName(e.target.value)}
                              placeholder="Samsung Galaxy S10"
                            />
                            <TextField
                              size="small" label="Android Version" sx={{ width: 120 }}
                              value={sessionAndroidVersion}
                              onChange={(e) => setSessionAndroidVersion(e.target.value)}
                            />
                            <TextField
                              size="small" label="APK URL" sx={{ flex: 1, minWidth: 200 }}
                              value={sessionAppUrl}
                              onChange={(e) => setSessionAppUrl(e.target.value)}
                              placeholder="https://example.com/app.apk"
                            />
                          </>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Agent HITL Gate — shown when agent calls request_user_input */}
              {agentHitlGate && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 1,
                    maxWidth: activeTestingSession ? '100%' : 800,
                    mx: 'auto',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.50',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {agentHitlGate.message}
                  </Typography>

                  {/* Page selection UI */}
                  {agentHitlGate.eventType === 'page_selection_required' && agentHitlGate.data.pages && (
                    <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1 }}>
                      {(agentHitlGate.data.pages as any[]).map((page: any, i: number) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <input
                            type="checkbox"
                            defaultChecked
                            id={`hitl-page-${i}`}
                            data-url={page.url}
                          />
                          <label htmlFor={`hitl-page-${i}`} style={{ flex: 1, fontSize: '0.85rem' }}>
                            <strong>{page.title || page.url}</strong>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {page.url} — {page.forms_count || 0} forms, {page.links_count || 0} links
                              {page.is_login_page && ' (login)'}
                            </Typography>
                          </label>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Test case review UI — editable plan */}
                  {agentHitlGate.eventType === 'test_case_review_required' && agentHitlGate.data.test_cases && (
                    <EditableTestPlanCard
                      testCases={agentHitlGate.data.test_cases as any[]}
                      onApprove={(cases) =>
                        handleAgentHitlResponse({ approved: true, edited: true, test_cases: cases })
                      }
                      onSkip={() => handleAgentHitlResponse({ approved: false, cancelled: true })}
                    />
                  )}

                  {/* Credentials input UI */}
                  {agentHitlGate.eventType === 'credentials_required' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        label="Username / Email / Phone"
                        id="hitl-cred-username"
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Password"
                        type="password"
                        id="hitl-cred-password"
                        fullWidth
                      />
                    </Box>
                  )}

                  {/* Free text input UI (OTP, etc.) */}
                  {agentHitlGate.eventType === 'free_text_required' && (
                    <Box sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        label={agentHitlGate.data.label || agentHitlGate.message || 'Enter value'}
                        id="hitl-free-text"
                        fullWidth
                        autoFocus
                      />
                    </Box>
                  )}

                  {/* Security Portal findings → TMS issue(s) approval */}
                  {agentHitlGate.eventType === 'security_create_issue' && (
                    <Box sx={{ mb: 1 }} data-testid="hitl-security-findings">
                      {agentHitlGate.data.pending ? (
                        <Typography variant="body2" color="text.secondary">
                          Loading the findings to approve…
                        </Typography>
                      ) : agentHitlGate.data.unavailable ? (
                        <Typography variant="body2" color="text.secondary">
                          Nothing will be filed unless you approve it.
                        </Typography>
                      ) : (
                        ((agentHitlGate.data.findings as any[]) || []).map((f: any) => (
                          <Typography key={f.id} variant="body2">
                            [{f.severity}] {f.title}
                          </Typography>
                        ))
                      )}
                    </Box>
                  )}

                  {/* Large load test approval (cap: 25 users, 120 s) */}
                  {agentHitlGate.eventType === 'load_large' && (
                    <Box sx={{ mb: 1 }} data-testid="hitl-load-large">
                      <Typography variant="body2">{agentHitlGate.data.subject || 'Load test'}</Typography>
                      <Typography variant="body2">
                        Peak users: {agentHitlGate.data.vus} · Duration: {agentHitlGate.data.duration_s} s
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        The chat limit is 25 users and 120 s. Approving lets this one request run.
                      </Typography>
                    </Box>
                  )}

                  {/* Confirmation UI */}
                  {agentHitlGate.eventType === 'confirmation_required' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {agentHitlGate.data.details || 'Please confirm to proceed.'}
                    </Typography>
                  )}

                  {/* The editable test-plan card renders its own actions. */}
                  {agentHitlGate.eventType !== 'test_case_review_required' && (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={agentHitlGate.eventType === 'security_create_issue' && !agentHitlGate.requestId}
                      onClick={() => {
                        if (agentHitlGate.eventType === 'security_create_issue' || agentHitlGate.eventType === 'load_large') {
                          handleAgentHitlResponse({ approved: false, decision: 'deny' });
                        } else {
                          handleAgentHitlResponse({ approved: false, cancelled: true });
                        }
                      }}
                    >
                      {agentHitlGate.eventType === 'security_create_issue' || agentHitlGate.eventType === 'load_large' ? 'Decline' : 'Skip'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={agentHitlGate.eventType === 'security_create_issue' && !agentHitlGate.requestId}
                      onClick={() => {
                        if (agentHitlGate.eventType === 'page_selection_required') {
                          const checkboxes = document.querySelectorAll<HTMLInputElement>('[id^="hitl-page-"]');
                          const selected = Array.from(checkboxes)
                            .filter((cb) => cb.checked)
                            .map((cb) => cb.dataset.url);
                          handleAgentHitlResponse({ approved: true, selected_urls: selected });
                        } else if (agentHitlGate.eventType === 'credentials_required') {
                          const usernameEl = document.getElementById('hitl-cred-username') as HTMLInputElement;
                          const passwordEl = document.getElementById('hitl-cred-password') as HTMLInputElement;
                          handleAgentHitlResponse({
                            approved: true,
                            credentials: {
                              username: usernameEl?.value || '',
                              password: passwordEl?.value || '',
                            },
                          });
                        } else if (agentHitlGate.eventType === 'free_text_required') {
                          const textEl = document.getElementById('hitl-free-text') as HTMLInputElement;
                          handleAgentHitlResponse({
                            approved: true,
                            text: textEl?.value || '',
                          });
                        } else if (agentHitlGate.eventType === 'test_case_review_required') {
                          handleAgentHitlResponse({ approved: true });
                        } else {
                          handleAgentHitlResponse({ approved: true });
                        }
                      }}
                    >
                      {agentHitlGate.eventType === 'page_selection_required' ? 'Test Selected Pages' :
                       agentHitlGate.eventType === 'credentials_required' ? 'Login' :
                       agentHitlGate.eventType === 'free_text_required' ? 'Submit' :
                       agentHitlGate.eventType === 'security_create_issue' ? 'Create issue(s)' :
                       agentHitlGate.eventType === 'load_large' ? 'Approve' :
                       'Confirm'}
                    </Button>
                  </Box>
                  )}
                </Paper>
              )}

              <Paper
                elevation={2}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 1,
                  maxWidth: activeTestingSession ? '100%' : 800,
                  mx: 'auto',
                  borderRadius: 3,
                }}
              >
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={activeTestingSession ? 3 : 6}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={activeTestingSession
                    ? "Type a command (approve, reject, stop) or ask a question..."
                    : sessionJustCompleted
                    ? "Refine tests... e.g. 'fix tests to share browser context', 'add error handling'"
                    : "Message QA AI Agent..."}
                  disabled={isLoading || agentStatus === 'offline'}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{ mx: activeTestingSession ? 1 : 2 }}
                />
                <IconButton
                  color="primary"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading || agentStatus === 'offline'}
                  sx={{
                    bgcolor: input.trim() ? 'primary.main' : 'transparent',
                    color: input.trim() ? 'white' : 'action.disabled',
                    '&:hover': { bgcolor: input.trim() ? 'primary.dark' : 'transparent' },
                    '&:disabled': { bgcolor: 'transparent' },
                    transition: 'all 0.2s',
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                </IconButton>
              </Paper>
              {activeTestingSession ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                  Commands: approve, reject, stop, skip login, solve captcha, retry
                </Typography>
              ) : sessionJustCompleted ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                  Type feedback to refine generated tests, or ask follow-up questions about the session
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Press Enter to send, Shift+Enter for new line
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Resizable Divider — drag handle between chat and right panel */}
        {hasRightPanel && (
          <Box
            onMouseDown={handleDividerMouseDown}
            sx={{
              width: 8,
              minWidth: 8,
              cursor: 'col-resize',
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              zIndex: 10,
              borderLeft: '1px solid',
              borderRight: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'primary.100',
                '& .divider-grip': { bgcolor: 'primary.main' },
              },
              '&:active': {
                bgcolor: 'primary.200',
              },
            }}
          >
            <Box
              className="divider-grip"
              sx={{
                width: 4,
                height: 48,
                borderRadius: 2,
                bgcolor: 'grey.500',
                transition: 'background-color 0.2s',
              }}
            />
          </Box>
        )}

        {/* Right Panel — Test Plan Editor, Browser Pane (live stream), or API Execution Panel */}
        {hasRightPanel && (
          <Box
            sx={{
              flexGrow: 1,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {isTestPlanPanel ? (
              <TestPlanEditor
                testPlan={activeTestPlanDraft!}
                onChange={setActiveTestPlanDraft}
                onSave={handleSaveTestPlan}
                onDiscard={handleDiscardTestPlan}
                isSaving={isTestPlanSaving}
              />
            ) : isTestPlanDetailPanel ? (
              <TestPlanDetailPanel
                plans={testPlanDetailItems!}
                selectedPlan={testPlanDetailSelected}
                onSelectPlan={setTestPlanDetailSelected}
                onClose={() => {
                  setTestPlanDetailItems(null);
                  setTestPlanDetailSelected(null);
                }}
                onAskAgent={(prompt) => {
                  setInput(prompt);
                  inputRef.current?.focus();
                }}
              />
            ) : isPageSelectionPanel ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Select Pages to Test
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose which pages should be deep-analyzed and tested. Pages with forms are pre-selected.
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <SiteTreeViewer
                    siteTree={testingSession.siteTree}
                    coverage={null}
                    nodeScreenshots={testingSession.nodeScreenshots}
                    selectionMode={true}
                    discoveredPages={testingSession.discoveredPages}
                    onConfirmSelection={testingSession.submitPageSelection}
                  />
                </Box>
              </Box>
            ) : isSlaveLiveStreamPanel ? (
              <UITestingPanel
                projectId={currentProject?.id || null}
                panelFramework={slaveFramework || null}
                forceInteractiveView={!!agentHitlGate?.interactionRequired}
                liveStreamUrl={effectiveSlaveLiveStream?.liveStreamUrl || null}
                vncUrl={effectiveSlaveLiveStream?.vncUrl || null}
                livekitToken={effectiveSlaveLiveStream?.livekitToken || null}
                livekitUrl={effectiveSlaveLiveStream?.livekitUrl || null}
                livekitRoom={effectiveSlaveLiveStream?.livekitRoom || null}
                phase={slavePhase === 'completed' ? 'completed' : slavePhase === 'failed' ? 'failed' : 'executing'}
                duration={0}
                url=""
                steps={slaveApiResults.map((r, i) => ({
                  index: i,
                  description: r.description || `Test ${i + 1}`,
                  action_id: r.action_id || `test-${i}`,
                  parameters: {},
                  status: (r.status === 'passed' ? 'passed' : r.status === 'running' ? 'running' : 'failed') as 'pending' | 'running' | 'passed' | 'failed',
                }))}
                stepResults={slaveApiResults.map((r, i) => ({
                  step_index: i,
                  action_id: r.action_id || `test-${i}`,
                  status: r.status === 'passed' ? 'passed' : r.status === 'running' ? 'running' : 'failed',
                  duration_ms: r.duration_ms,
                  error: typeof r.error === 'string' ? r.error : r.error ? JSON.stringify(r.error) : undefined,
                }))}
                generatedCode={slaveGeneratedCode ? {
                  files: slaveGeneratedCode.files || [],
                  summary: typeof slaveGeneratedCode.summary === 'string'
                    ? slaveGeneratedCode.summary
                    : `${slaveGeneratedCode.summary?.total_files || 0} files, ${slaveGeneratedCode.summary?.total_test_cases || 0} tests`,
                  framework: 'playwright',
                } : null}
                summary={slavePhase === 'completed' ? {
                  total_steps: slaveApiResults.length,
                  passed: slaveApiResults.filter(r => r.status === 'passed').length,
                  failed: slaveApiResults.filter(r => r.status !== 'passed').length,
                } : null}
                qualityAssessment={null}
                onEndSession={() => {
                  setSlaveLiveStream(null);
                  provisionBrowserRef.current = null;
                  setSlaveApiResults([]);
                  setSlavePhase('');
                  setSlaveGeneratedCode(null);
                  setSlaveTmsIntegration(null);
                  setSlaveDiscoveredEndpoints([]);
                  setSlaveFramework('');
                }}
                onCancel={() => {
                  setSlaveLiveStream(null);
                  provisionBrowserRef.current = null;
                  setSlaveApiResults([]);
                  setSlavePhase('');
                  setSlaveGeneratedCode(null);
                  setSlaveTmsIntegration(null);
                  setSlaveDiscoveredEndpoints([]);
                  setSlaveFramework('');
                }}
              />
            ) : isApiPanel ? (
              <APIExecutionPanel
                apiStepResults={hasSlaveApiPanel ? slaveApiResults : testingSession.apiStepResults}
                phase={hasSlaveApiPanel ? slavePhase : testingSession.phase}
                generatedCode={hasSlaveApiPanel ? slaveGeneratedCode : testingSession.generatedCode}
                agentActive={hasSlaveApiPanel ? isStreaming : testingSession.agentActive}
                agentTask={hasSlaveApiPanel ? (slaveFramework === 'playwright' || slaveFramework === 'selenium' ? 'UI Testing' : 'API Testing') : testingSession.agentTask}
                agentToolEvents={hasSlaveApiPanel ? slaveAgentToolEvents : testingSession.agentToolEvents}
                agentFindings={hasSlaveApiPanel ? null : testingSession.agentFindings}
                tmsIntegration={slaveTmsIntegration}
                projectId={currentProject?.id}
                discoveredEndpoints={slaveDiscoveredEndpoints}
                framework={hasSlaveApiPanel ? slaveFramework : undefined}
                onEndSession={() => {
                  if (hasSlaveApiPanel) {
                    setSlaveApiResults([]);
                    setSlavePhase('');
                    setSlaveGeneratedCode(null);
                    setSlaveTmsIntegration(null);
                    setSlaveDiscoveredEndpoints([]);
                    setSlaveFramework('');
                  } else {
                    testingSession.endSession();
                    setActiveTestingSession(null);
                    localStorage.removeItem('qa_agent_active_testing_session');
                  }
                }}
                onCancel={() => {
                  if (hasSlaveApiPanel) {
                    setSlaveApiResults([]);
                    setSlavePhase('');
                    setSlaveGeneratedCode(null);
                    setSlaveTmsIntegration(null);
                    setSlaveDiscoveredEndpoints([]);
                    setSlaveFramework('');
                  } else {
                    testingSession.cancel();
                    setActiveTestingSession(null);
                    localStorage.removeItem('qa_agent_active_testing_session');
                  }
                }}
              />
            ) : isSelectedApiPanel && selectedPanelData ? (
              /* Mode B: Per-message artifact panel — API results / generated code */
              <APIExecutionPanel
                apiStepResults={selectedPanelData.apiResults}
                phase={selectedPanelData.phase}
                generatedCode={selectedPanelData.generatedCode as GeneratedCodePayload | null}
                agentActive={false}
                agentTask="API Testing"
                agentToolEvents={selectedPanelData.agentToolEvents}
                agentFindings={null}
                tmsIntegration={null}
                projectId={currentProject?.id}
                discoveredEndpoints={[]}
                onEndSession={() => setSelectedMessageId(null)}
                onCancel={() => setSelectedMessageId(null)}
              />
            ) : isSelectedTestPlanPanel && selectedPanelData?.testPlanDraft ? (
              /* Mode B: Per-message artifact panel — Test plan draft (read-only view) */
              <TestPlanEditor
                testPlan={selectedPanelData.testPlanDraft}
                onChange={() => {}}
                onSave={() => {}}
                onDiscard={() => setSelectedMessageId(null)}
                isSaving={false}
              />
            ) : (
              <UITestingPanel
                liveStreamUrl={testingSession.liveStreamUrl}
                vncUrl={testingSession.vncUrl}
                livekitToken={testingSession.livekitToken}
                livekitUrl={testingSession.livekitUrl}
                livekitRoom={testingSession.livekitRoom}
                phase={testingSession.phase}
                duration={testingSession.duration}
                url={activeTestingSession!.url}
                steps={testingSession.steps}
                stepResults={testingSession.stepResults}
                generatedCode={testingSession.generatedCode}
                summary={testingSession.summary}
                qualityAssessment={testingSession.qualityAssessment}
                currentPageIndex={testingSession.currentPageIndex}
                totalPages={testingSession.totalPages}
                currentPageTitle={testingSession.currentPageTitle}
                pageResults={testingSession.pageResults}
                isTestCaseReviewPending={testingSession.isTestCaseReviewPending}
                currentPageTestCases={testingSession.currentPageTestCases}
                isCodeReviewPending={testingSession.isCodeReviewPending}
                currentPageCode={testingSession.currentPageCode}
                onApproveTestCases={testingSession.approveTestCases}
                onApproveCode={testingSession.approveCode}
                isCredentialRequested={testingSession.isCredentialRequested}
                loginFields={testingSession.loginFields}
                onSubmitCredentials={(u, p) => testingSession.submitCredentials(u, p)}
                onSkipLogin={() => testingSession.skipLogin()}
                onEndSession={() => {
                  testingSession.endSession();
                  setActiveTestingSession(null);
                  localStorage.removeItem('qa_agent_active_testing_session');
                }}
                onCancel={() => {
                  testingSession.cancel();
                  setActiveTestingSession(null);
                  localStorage.removeItem('qa_agent_active_testing_session');
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Tools & Tips Dialog */}
      <Dialog
        open={showToolsDialog}
        onClose={() => setShowToolsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Available Tools & Quick Actions</Typography>
          <IconButton onClick={() => setShowToolsDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Click on any tool or quick action to use it as a prompt
          </Typography>

          {/* Quick Actions */}
          <Typography variant="h6" sx={{ mb: 1 }}>Quick Actions</Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {QUICK_ACTIONS.map((category) => (
              <Grid item xs={12} sm={6} key={category.category}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {category.category}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {category.prompts.map((prompt) => (
                    <Chip
                      key={prompt}
                      label={prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt}
                      size="small"
                      onClick={() => handleQuickPrompt(prompt)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Tool Categories */}
          <Typography variant="h6" sx={{ mb: 1 }}>Tool Categories</Typography>
          {TOOL_CATEGORIES.map((category) => (
            <Accordion
              key={category.name}
              expanded={selectedCategory === category.name}
              onChange={(_, expanded) => setSelectedCategory(expanded ? category.name : null)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getToolIcon(category.icon)}
                  <Typography>{category.name}</Typography>
                  <Chip size="small" label={category.tools.length} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {category.description}
                </Typography>
                <List dense disablePadding>
                  {category.tools.map((tool) => (
                    <ListItem key={tool.name} disablePadding>
                      <ListItemButton onClick={() => handleQuickPrompt(tool.example_prompt)} sx={{ borderRadius: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <PlayIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={tool.name.replace(/_/g, ' ')}
                          secondary={tool.description}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Tips */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Tips</Typography>
            <Typography variant="body2">
              - Be specific about what you need<br />
              - Include IDs when referencing items (e.g., TC-123, TR-456)<br />
              - Ask follow-up questions for clarification
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatPage;
